<template>
  <div id="jcsat-kit-purchase" class="premium-purchase-experience">
    <!-- Background Moving Gradients & Shapes -->
    <div class="animated-mesh-bg"></div>
    <div class="background-decorations">
      <div class="blur-blob blob-1"></div>
      <div class="blur-blob blob-2"></div>
      <div class="blur-blob blob-3"></div>
    </div>

    <!-- Sticky Mobile checkout bar -->
    <div class="mobile-sticky-footer d-md-none" v-if="TotalQuantity >= 50">
      <div
        class="mobile-footer-content px-4 py-3 d-flex align-center justify-space-between"
      >
        <div>
          <span class="mobile-lbl text-uppercase">Total Quantity</span>
          <span class="mobile-val font-weight-bold d-block"
            >{{ TotalQuantity }} Kits</span
          >
        </div>
        <div class="text-right">
          <span class="mobile-lbl text-uppercase">Grand Total (incl. GST)</span>
          <span class="mobile-price font-weight-black d-block"
            >₹
            {{
              GrandTotal.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            }}</span
          >
        </div>
        <v-btn
          @click="scrollToCheckout"
          color="white"
          class="checkout-trigger-btn font-weight-bold ml-2"
          rounded
          elevation="2"
        >
          Pay Now
          <v-icon right size="16">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-container class="py-6 px-4 max-width-container">
      <!-- HERO BANNER -->
      <div
        class="hero-banner slide-up-animation"
        :class="{ 'hero-banner-collapsed': isHeroCollapsed }"
      >
        <!-- Floating Particles -->
        <template v-if="!isHeroCollapsed">
          <div
            class="particle"
            v-for="n in 6"
            :key="'p' + n"
            :style="getParticleStyle(n)"
          ></div>
        </template>

        <!-- Collapse Trigger Button -->
        <button
          class="hero-collapse-btn"
          @click.stop="isHeroCollapsed = !isHeroCollapsed"
          :title="isHeroCollapsed ? 'Expand Header' : 'Collapse Header'"
        >
          <v-icon color="white" small :class="{ 'rotate-180': isHeroCollapsed }"
            >mdi-chevron-up</v-icon
          >
        </button>

        <!-- Collapsed Header Content -->
        <div
          v-if="isHeroCollapsed"
          class="hero-collapsed-content d-flex align-center justify-space-between w-100 py-1"
          @click="isHeroCollapsed = false"
        >
          <div class="d-flex align-center">
            <v-icon color="#fbbf24" class="mr-2" size="20"
              >mdi-school-outline</v-icon
            >
            <span class="collapsed-title font-weight-bold"
              >JCI Scholarship &amp; Aptitude Test (JCSAT)</span
            >
            <span class="collapsed-subtitle ml-4 d-none d-sm-inline opacity-75"
              >• National Learning Initiative</span
            >
          </div>
          <div class="d-flex align-center mr-8">
            <span class="expand-prompt text-caption font-weight-bold mr-1"
              >Expand Info</span
            >
            <v-icon color="white" small>mdi-chevron-down</v-icon>
          </div>
        </div>

        <!-- Full Header Content -->
        <v-expand-transition>
          <div v-show="!isHeroCollapsed">
            <v-row class="align-center position-relative">
              <v-col cols="12" md="8" class="hero-text-col">
                <div class="badge-hero mb-3">
                  <v-icon small color="white" class="mr-1"
                    >mdi-school-outline</v-icon
                  >
                  National Learning Initiative
                </div>
                <h1 class="hero-title">
                  JCI Scholarship &amp;
                  <span class="text-gradient-sky">Aptitude Test (JCSAT)</span>
                </h1>
                <p class="hero-subtitle">
                  Empowering Students Through Learning Excellence &amp; Academic
                  Recognition. Purchase official kit packages below to register
                  student participants.
                </p>
              </v-col>

              <v-col
                cols="12"
                md="4"
                class="d-none d-md-flex justify-center position-relative"
              >
                <!-- Premium Student Illustration (SVG-based) -->
                <div class="illustration-container">
                  <svg
                    class="hero-svg-art"
                    viewBox="0 0 200 200"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient
                        id="hero-grad-1"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stop-color="#ffffff" />
                        <stop offset="100%" stop-color="#e0f2fe" />
                      </linearGradient>
                      <linearGradient
                        id="hero-grad-2"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stop-color="#fbbf24" />
                        <stop offset="100%" stop-color="#f59e0b" />
                      </linearGradient>
                    </defs>
                    <!-- Outer floating ring -->
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      stroke="#ffffff"
                      stroke-opacity="0.3"
                      stroke-width="1.5"
                      stroke-dasharray="6 6"
                      class="spin-animation"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="65"
                      fill="#ffffff"
                      fill-opacity="0.1"
                    />
                    <!-- Open book base -->
                    <path
                      d="M50 145 C 50 145, 80 135, 100 145 C 120 135, 150 145, 150 145 L 150 95 C 150 95, 120 85, 100 95 C 80 85, 50 95, 50 95 Z"
                      fill="url(#hero-grad-1)"
                    />
                    <path
                      d="M100 95 L 100 145"
                      stroke="#1e3a8a"
                      stroke-width="2"
                    />
                    <!-- Graduation cap -->
                    <path
                      d="M100 45 L 145 60 L 100 75 L 55 60 Z"
                      fill="url(#hero-grad-2)"
                      stroke="#ffffff"
                      stroke-width="2"
                    />
                    <path
                      d="M78 68 L 78 85 C90 92 110 92 122 85 L 122 68"
                      fill="url(#hero-grad-2)"
                      stroke="#ffffff"
                      stroke-width="2"
                    />
                    <line
                      x1="135"
                      y1="63"
                      x2="135"
                      y2="88"
                      stroke="#fbbf24"
                      stroke-width="2"
                    />
                    <circle cx="135" cy="88" r="3" fill="#fbbf24" />
                    <!-- Glowing lamp of knowledge -->
                    <path
                      d="M100 10 L108 22 H92 Z"
                      fill="#fbbf24"
                      class="pulse-animation"
                    />
                    <circle
                      cx="100"
                      cy="28"
                      r="6"
                      fill="#fbbf24"
                      class="pulse-animation"
                    />
                  </svg>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </div>

      <!-- MAIN PAGE GRID -->
      <v-row class="mt-8">
        <!-- LEFT COLUMN: MEMBER DETAILS & PRODUCTS -->
        <v-col cols="12" lg="8" md="7" class="left-content-column">
          <!-- MEMBER INFORMATION glass card -->
          <v-card
            class="member-profile-card slide-up-animation delay-100 mb-8"
            elevation="1"
          >
            <!-- Badge and Collapse Trigger -->
            <div
              class="d-flex align-center justify-space-between w-100 pr-10"
              style="min-height: 28px; position: relative"
            >
              <div class="profile-header-badge d-flex align-center">
                <span class="badge-status-dot"></span>
                <v-icon x-small color="#10B981" class="mr-1"
                  >mdi-shield-check</v-icon
                >
                Verified Active LOM Member Account
              </div>
              <v-btn
                icon
                small
                class="member-collapse-btn-toggle"
                @click.stop="isMemberCollapsed = !isMemberCollapsed"
                style="position: absolute; right: -8px; top: -10px; z-index: 10"
                :title="
                  isMemberCollapsed
                    ? 'Expand Account details'
                    : 'Collapse Account details'
                "
              >
                <v-icon :class="{ 'rotate-180': !isMemberCollapsed }"
                  >mdi-chevron-down</v-icon
                >
              </v-btn>
            </div>

            <!-- Collapsed summary preview -->
            <div
              v-if="isMemberCollapsed"
              class="member-collapsed-summary mt-4"
              @click="isMemberCollapsed = false"
            >
              <div class="d-flex align-center justify-space-between flex-wrap">
                <span class="collapsed-member-name font-weight-bold">{{
                  Member.MemberName
                }}</span>
                <span class="collapsed-member-lom">{{
                  Member.LomName || "JCI Local Organization"
                }}</span>
              </div>
            </div>

            <v-expand-transition>
              <div v-show="!isMemberCollapsed">
                <v-row class="align-center mt-4">
                  <v-col
                    cols="12"
                    sm="3"
                    class="d-flex justify-center justify-sm-start"
                  >
                    <div class="avatar-glow">
                      <div class="profile-avatar-initials">
                        <img
                          v-if="Member.MemberImage"
                          :src="Member.MemberImage"
                          :alt="Member.MemberName"
                          class="profile-avatar-img"
                        />
                        <span v-else>{{ getInitials(Member.MemberName) }}</span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="9" class="text-center text-sm-left">
                    <h2 class="member-title-name">{{ Member.MemberName }}</h2>
                    <div
                      class="member-subtitle-id d-flex align-center justify-center justify-sm-start mt-1"
                    >
                      <span class="id-pill"
                        >ID: {{ Member.MembershipId || "LOM-MEMBER" }}</span
                      >
                      <span class="divider-dot mx-2"></span>
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="my-6 border-light-white"></v-divider>

                <!-- Detailed Grid -->
                <v-row>
                  <v-col cols="12" sm="6" md="6" class="py-2">
                    <div class="member-field-item">
                      <v-icon small color="#60A5FA" class="mr-3"
                        >mdi-office-building</v-icon
                      >
                      <div>
                        <label class="field-lbl">LOM Name</label>
                        <span class="field-val">{{
                          Member.LomName || "JCI Local Organization"
                        }}</span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" class="py-2">
                    <div class="member-field-item">
                      <v-icon small color="#60A5FA" class="mr-3"
                        >mdi-map-marker-radius</v-icon
                      >
                      <div>
                        <label class="field-lbl">Zone Affiliation</label>
                        <span class="field-val">{{
                          Member.ZoneName || "Zone XX"
                        }}</span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" class="py-2">
                    <div class="member-field-item">
                      <v-icon small color="#60A5FA" class="mr-3"
                        >mdi-phone-outline</v-icon
                      >
                      <div>
                        <label class="field-lbl">Primary Mobile</label>
                        <span class="field-val">{{
                          Member.PrimaryMobile || "+91 98765 43210"
                        }}</span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" class="py-2">
                    <div class="member-field-item">
                      <v-icon small color="#60A5FA" class="mr-3"
                        >mdi-email-outline</v-icon
                      >
                      <div>
                        <label class="field-lbl">Email Address</label>
                        <span class="field-val">{{
                          Member.EmailId || "member@jciindia.in"
                        }}</span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
          </v-card>

          <!-- MEMBER LOCATION CARD -->
          <v-card
            class="member-profile-card slide-up-animation delay-100 mb-8"
            elevation="4"
          >
            <div
              class="member-header d-flex align-center justify-space-between mb-4 w-100"
              style="position: relative"
            >
              <div class="d-flex align-center">
                <v-icon color="#1565C0" large class="mr-4"
                  >mdi-map-marker-multiple</v-icon
                >
                <div>
                  <h3
                    class="member-card-title mb-0"
                    style="font-size: 20px; font-weight: 800; color: #0f172a"
                  >
                    Member Location Information
                  </h3>
                  <span
                    v-if="!isLocationCollapsed"
                    style="font-size: 13px; color: #64748b; font-weight: 500"
                    >Select State, District, and City to calculate the
                    appropriate tax rates.</span
                  >
                </div>
              </div>
              <v-btn
                icon
                small
                class="location-collapse-btn-toggle"
                @click.stop="isLocationCollapsed = !isLocationCollapsed"
                style="position: absolute; right: -8px; top: 0; z-index: 10"
                :title="
                  isLocationCollapsed
                    ? 'Expand Location Information'
                    : 'Collapse Location Information'
                "
              >
                <v-icon :class="{ 'rotate-180': !isLocationCollapsed }"
                  >mdi-chevron-down</v-icon
                >
              </v-btn>
            </div>

            <!-- Collapsed summary preview -->
            <div
              v-if="isLocationCollapsed"
              class="member-collapsed-summary mt-2"
              @click="isLocationCollapsed = false"
            >
              <div class="d-flex align-center justify-space-between flex-wrap">
                <span class="collapsed-location-text font-weight-bold">
                  📍 {{ getSelectedLocationText() }}
                </span>
                <span
                  class="collapsed-tax-rate text-caption font-weight-bold"
                  style="color: #1565c0"
                >
                  Tax Rate: {{ inter_state ? "18% IGST" : "18% CGST+SGST" }}
                </span>
              </div>
            </div>

            <v-expand-transition>
              <div v-show="!isLocationCollapsed">
                <v-divider class="my-4 border-light-white"></v-divider>

                <v-row wrap>
                  <v-col cols="12" sm="4" class="py-2">
                    <label class="field-label-custom">
                      <span class="text-danger">*</span> State Of Member
                    </label>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      :loading="StateCodeOptionsLoading"
                      :items="StateCodeOptions"
                      :rules="StateCodeRules"
                      v-model="StateCode"
                      required
                      outlined
                      dense
                      rounded
                      prepend-inner-icon="mdi-map"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="4" class="py-2">
                    <label class="field-label-custom">
                      <span class="text-danger">*</span> District Of Member
                    </label>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      :loading="DistrictCodeOptionsLoading"
                      :items="DistrictCodeOptions"
                      :rules="DistrictCodeRules"
                      v-model="DistrictCode"
                      required
                      outlined
                      dense
                      rounded
                      prepend-inner-icon="mdi-city"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="4" class="py-2">
                    <label class="field-label-custom">
                      <span class="text-danger">*</span> City Of Member
                    </label>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      :loading="CityCodeOptionsLoading"
                      :items="CityCodeOptions"
                      :rules="CityCodeRules"
                      v-model="CityCode"
                      required
                      outlined
                      dense
                      rounded
                      prepend-inner-icon="mdi-map-marker"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
          </v-card>

          <!-- PRODUCT PURCHASE SECTION -->
          <div class="products-section-title mb-4 slide-up-animation delay-200">
            <h2 class="section-main-heading">Select JCSAT Kits</h2>
            <p class="section-sub-heading">
              Choose matching levels &amp; languages. Minimum order of
              <b>50 kits</b> is required per plan to generate a batch code.
            </p>
          </div>

          <v-row class="slide-up-animation delay-200">
            <v-col
              cols="12"
              sm="6"
              v-for="product in products"
              :key="product.id"
              class="mb-6"
            >
              <!-- Product Card -->
              <div
                :class="[
                  'product-card',
                  product.quantity >= product.minQty
                    ? 'product-card-selected'
                    : '',
                ]"
                @click="selectProduct(product)"
              >
                <!-- Selected Badge Indicator -->
                <div
                  class="selected-checkmark-badge"
                  v-if="product.quantity >= product.minQty"
                >
                  <v-icon small color="white">mdi-check-bold</v-icon>
                </div>

                <!-- Product illustration header -->
                <div
                  class="product-illustration-wrapper mb-3"
                  :style="{ backgroundColor: product.accent }"
                >
                  <!-- Premium Icon Badge -->
                  <div
                    class="premium-icon-badge-container"
                    :style="{
                      color: product.color,
                      boxShadow: '0 8px 20px ' + product.color + '33',
                    }"
                  >
                    <v-icon
                      size="38"
                      :color="product.color"
                      class="premium-badge-icon"
                    >
                      {{ product.icon }}
                    </v-icon>
                  </div>
                </div>

                <div
                  class="product-meta-row d-flex align-center justify-space-between mb-1"
                >
                  <span
                    class="product-medium-badge"
                    :style="{
                      color: product.color,
                      backgroundColor: product.accent,
                    }"
                  >
                    {{ product.lang }} Medium
                  </span>
                  <span class="product-status-pill text-caption">
                    <span class="status-dot green-dot"></span>
                    {{ product.status }}
                  </span>
                </div>

                <h3 class="product-title">{{ product.title }}</h3>
                <p class="product-classes text-caption mb-3">
                  {{ product.classes }}
                </p>

                <div class="d-flex align-center justify-space-between mt-4">
                  <div>
                    <span class="product-price-label text-caption d-block"
                      >Price Per Kit</span
                    >
                    <span class="product-price-val">₹{{ product.price }}</span>
                  </div>

                  <!-- Add to Cart / Quantity Selector -->
                  <div class="qty-control-wrapper">
                    <!-- Add Button -->
                    <v-btn
                      v-if="product.quantity === 0"
                      @click.stop="incrementProduct(product)"
                      color="#1565C0"
                      dark
                      rounded
                      class="add-to-cart-btn px-5"
                      elevation="2"
                    >
                      <v-icon left size="20">mdi-plus</v-icon>
                      <span class="add-plan-btn-text">Add Plan</span>
                    </v-btn>

                    <!-- Minus-Quantity-Plus Selector -->
                    <div v-else class="qty-stepper d-flex align-center">
                      <v-btn
                        icon
                        small
                        color="#1565C0"
                        class="qty-step-btn"
                        @click.stop="decrementProduct(product)"
                      >
                        <v-icon>{{
                          product.quantity === product.minQty
                            ? "mdi-delete-outline"
                            : "mdi-minus"
                        }}</v-icon>
                      </v-btn>
                      <input
                        type="number"
                        class="qty-type-input mx-2"
                        :min="product.minQty"
                        :value="product.quantity"
                        @change.stop="
                          (e) => {
                            const v = parseInt(e.target.value);
                            product.quantity =
                              v >= product.minQty ? v : product.minQty;
                          }
                        "
                        @click.stop
                      />
                      <v-btn
                        icon
                        small
                        color="#1565C0"
                        class="qty-step-btn"
                        @click.stop="incrementProduct(product)"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>

                <!-- Quick Presets -->
                <div
                  class="quick-qty-presets mt-4 pt-3 border-top-dash"
                  v-if="product.quantity >= product.minQty"
                >
                  <div class="d-flex align-center justify-space-between mb-2">
                    <span class="preset-title">⚡ Bulk Presets:</span>
                  </div>
                  <div class="preset-buttons d-flex">
                    <v-btn
                      rounded
                      outlined
                      color="#1565C0"
                      class="preset-btn font-weight-bold mr-2 flex-grow-1"
                      @click.stop="addQty(product, 50)"
                      >+50</v-btn
                    >
                    <v-btn
                      rounded
                      outlined
                      color="#1565C0"
                      class="preset-btn font-weight-bold mr-2 flex-grow-1"
                      @click.stop="addQty(product, 100)"
                      >+100</v-btn
                    >
                    <v-btn
                      rounded
                      outlined
                      color="#1565C0"
                      class="preset-btn font-weight-bold flex-grow-1"
                      @click.stop="addQty(product, 250)"
                      >+250</v-btn
                    >
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- PREMIUM ECOMMERCE DELIVERY SECTION (HIDDEN) -->
          <div
            class="delivery-section mt-8 mb-8 slide-up-animation delay-300"
            v-if="false"
          >
            <!-- Large Glassmorphism Card -->
            <v-card class="delivery-glass-card overflow-hidden" elevation="0">
              <!-- PREMIUM GRADIENT HEADER ATTACHED TO TOP OF CARD -->
              <div class="delivery-premium-header">
                <!-- Background Effects -->
                <div class="header-particle-track">
                  <span class="header-particle hp-1"></span>
                  <span class="header-particle hp-2"></span>
                  <span class="header-particle hp-3"></span>
                  <span class="header-particle hp-4"></span>
                  <span class="header-particle hp-5"></span>
                  <span class="header-particle hp-6"></span>
                </div>
                <div class="header-light-streaks">
                  <div class="streak streak-1"></div>
                  <div class="streak streak-2"></div>
                </div>
                <div class="header-glass-bubbles">
                  <span class="bubble bubble-1"></span>
                  <span class="bubble bubble-2"></span>
                  <span class="bubble bubble-3"></span>
                </div>
                <div class="header-glowing-circles">
                  <div class="gcircle gcircle-1"></div>
                  <div class="gcircle gcircle-2"></div>
                </div>
                <div class="header-shimmer"></div>
                <div class="header-light-rays"></div>

                <!-- Header Content Row -->
                <div class="header-main-content">
                  <!-- Left Side -->
                  <div class="header-left">
                    <div class="floating-icon-container">
                      <v-icon
                        color="white"
                        size="32"
                        class="floating-truck-icon"
                        >mdi-truck-fast</v-icon
                      >
                    </div>
                    <div class="header-text-container">
                      <h2 class="header-title">Delivery Information</h2>
                      <span class="header-subtitle"
                        >Provide shipping details for JCSAT Kit Delivery</span
                      >
                    </div>
                  </div>

                  <!-- Right Side -->
                  <div class="header-right">
                    <div class="premium-status-chip">
                      Required Before Payment
                    </div>
                    <div class="physical-delivery-tag">
                      📦 Physical Product Delivery
                    </div>
                  </div>
                </div>

                <!-- Header Stats Mini Badges -->
                <div class="header-stats-container">
                  <div class="stat-badge-card">
                    <span class="stat-badge-icon">📍</span>
                    <span class="stat-badge-text"
                      >Delivery Address Required</span
                    >
                  </div>
                  <div class="stat-badge-card">
                    <span class="stat-badge-icon">📦</span>
                    <span class="stat-badge-text">Secure Packaging</span>
                  </div>
                  <div class="stat-badge-card">
                    <span class="stat-badge-icon">🚚</span>
                    <span class="stat-badge-text">Fast Dispatch</span>
                  </div>
                </div>

                <!-- Bottom Strip -->
                <div class="header-bottom-strip">
                  <div class="strip-light-sweep"></div>
                </div>
              </div>

              <!-- Floating Corner Decorations & Glow -->
              <div class="glow-sphere-1"></div>
              <div class="glow-sphere-2"></div>
              <div class="corner-decor-top-right"></div>
              <div class="corner-decor-bottom-left"></div>

              <!-- Subtle Moving Particles -->
              <div class="particle-track">
                <div
                  class="delivery-particle"
                  v-for="n in 6"
                  :key="'dp' + n"
                  :style="getDeliveryParticleStyle(n)"
                ></div>
              </div>

              <!-- Floating flying shipping icons -->
              <div class="flying-icons-container">
                <v-icon class="flying-icon icon-truck"
                  >mdi-truck-fast-outline</v-icon
                >
                <v-icon class="flying-icon icon-box"
                  >mdi-package-variant</v-icon
                >
                <v-icon class="flying-icon icon-plane">mdi-airplane</v-icon>
                <v-icon class="flying-icon icon-map"
                  >mdi-map-marker-distance</v-icon
                >
                <v-icon class="flying-icon icon-email"
                  >mdi-email-fast-outline</v-icon
                >
              </div>

              <v-card-text class="pa-6 pa-sm-8">
                <v-row>
                  <!-- Form Fields Col -->
                  <v-col cols="12" md="7" class="pr-md-6">
                    <!-- Information Notice Banner -->
                    <div
                      class="delivery-info-banner mb-6 d-flex align-start pa-4"
                    >
                      <div class="info-pulse-icon-box mr-3">
                        <v-icon color="#1565C0"
                          >mdi-package-variant-closed</v-icon
                        >
                        <div class="pulse-ring"></div>
                      </div>
                      <div class="info-banner-content">
                        <h4 class="info-banner-title mb-1">
                          📦 Delivery Guidelines
                        </h4>
                        <ul class="info-banner-list pl-0">
                          <li>Enter complete delivery address.</li>
                          <li>Include building name, street, area and city.</li>
                          <li>
                            <strong
                              >PINCODE must be included in the address.</strong
                            >
                          </li>
                          <li>
                            Kits will be delivered to the address provided here.
                          </li>
                          <li>Incorrect addresses may delay dispatch.</li>
                        </ul>
                      </div>
                    </div>

                    <!-- Input 1: Contact Person Name -->
                    <div class="delivery-input-group mb-5">
                      <div
                        class="premium-input-wrapper"
                        :class="{
                          'is-focused': isNameFocused,
                          'is-filled': !!Delivery.ContactName,
                          'is-valid': isNameValid,
                          'has-error': showValidationErrors && !isNameValid,
                          'shake-anim': shakeFields && !isNameValid,
                        }"
                      >
                        <div class="input-glow-bg"></div>
                        <div class="input-inner">
                          <v-icon class="input-prefix-icon"
                            >mdi-account-outline</v-icon
                          >
                          <div class="input-field-container">
                            <input
                              type="text"
                              v-model="Delivery.ContactName"
                              class="custom-input-element"
                              placeholder=" "
                              @focus="isNameFocused = true"
                              @blur="isNameFocused = false"
                            />
                            <label class="custom-floating-label"
                              >Contact Person Name *</label
                            >
                          </div>
                          <br />
                          <div class="input-suffix-container">
                            <transition name="scale-fade">
                              <v-icon
                                v-if="isNameValid"
                                class="valid-checkmark"
                                color="#10B981"
                                >mdi-check-circle</v-icon
                              >
                            </transition>
                          </div>
                        </div>
                        <span class="custom-input-hint"
                          >Enter the person who will receive the kits</span
                        >
                      </div>
                    </div>

                    <!-- Input 2: Contact Mobile Number -->
                    <div class="delivery-input-group mb-5">
                      <div
                        class="premium-input-wrapper"
                        :class="{
                          'is-focused': isMobileFocused,
                          'is-filled': !!Delivery.ContactMobile,
                          'is-valid': isMobileValid,
                          'has-error': showValidationErrors && !isMobileValid,
                          'shake-anim': shakeFields && !isMobileValid,
                        }"
                      >
                        <div class="input-glow-bg"></div>
                        <div class="input-inner">
                          <v-icon class="input-prefix-icon"
                            >mdi-phone-outline</v-icon
                          >
                          <div class="input-field-container">
                            <input
                              type="tel"
                              v-model="Delivery.ContactMobile"
                              class="custom-input-element"
                              placeholder=" "
                              maxlength="10"
                              @input="
                                Delivery.ContactMobile =
                                  Delivery.ContactMobile.replace(
                                    /[^0-9]/g,
                                    ''
                                  ).slice(0, 10)
                              "
                              @focus="isMobileFocused = true"
                              @blur="isMobileFocused = false"
                            />
                            <label class="custom-floating-label"
                              >Contact Mobile Number *</label
                            >
                          </div>
                          <br />
                          <div class="input-suffix-container">
                            <transition name="scale-fade">
                              <v-icon
                                v-if="isMobileValid"
                                class="valid-checkmark"
                                color="#10B981"
                                >mdi-check-circle</v-icon
                              >
                            </transition>
                          </div>
                        </div>
                        <span class="custom-input-hint"
                          >Enter 10-digit mobile number (numbers only)</span
                        >
                      </div>
                    </div>

                    <!-- Input 3: Complete Delivery Address -->
                    <div class="delivery-input-group mb-5">
                      <div
                        class="premium-input-wrapper premium-input-wrapper-textarea"
                        :class="{
                          'is-focused': isAddressFocused,
                          'is-filled': !!Delivery.Address,
                          'is-valid': isAddressFieldValid,
                          'has-error':
                            showValidationErrors && !isAddressFieldValid,
                          'shake-anim': shakeFields && !isAddressFieldValid,
                        }"
                      >
                        <div class="input-glow-bg"></div>
                        <div class="input-inner">
                          <v-icon class="input-prefix-icon"
                            >mdi-map-marker-outline</v-icon
                          >
                          <div class="input-field-container">
                            <textarea
                              v-model="Delivery.Address"
                              class="custom-input-element custom-textarea-element"
                              placeholder=" "
                              rows="6"
                              @focus="isAddressFocused = true"
                              @blur="isAddressFocused = false"
                            ></textarea>
                            <label class="custom-floating-label"
                              >Complete Delivery Address *</label
                            >
                          </div>
                          <div class="input-suffix-container">
                            <transition name="scale-fade">
                              <v-icon
                                v-if="isAddressFieldValid && hasPincode"
                                class="valid-checkmark"
                                color="#10B981"
                                >mdi-check-circle</v-icon
                              >
                              <v-icon
                                v-else-if="isAddressFieldValid && !hasPincode"
                                class="valid-checkmark"
                                color="#F59E0B"
                                >mdi-alert-circle</v-icon
                              >
                            </transition>
                          </div>
                        </div>
                        <span class="custom-input-hint">
                          Example:<br />
                          JCI Madurai<br />
                          No 12, ABC Building<br />
                          West Masi Street<br />
                          Madurai - 625001<br />
                          Tamil Nadu
                        </span>
                      </div>
                    </div>
                  </v-col>

                  <!-- Live Address Preview Col -->
                  <v-col
                    cols="12"
                    md="5"
                    class="d-flex flex-column justify-space-between pl-md-6 border-left-premium-desktop"
                  >
                    <div class="delivery-preview-sticky">
                      <div
                        class="preview-header d-flex align-center justify-space-between mb-4"
                      >
                        <h4 class="preview-title d-flex align-center">
                          <span class="preview-icon mr-2">📍</span> Delivery
                          Preview
                        </h4>
                        <!-- Verification Badge -->
                        <transition name="scale-fade">
                          <div
                            class="verification-badge d-flex align-center"
                            v-if="isAddressValid"
                          >
                            <v-icon color="#10B981" small class="mr-1"
                              >mdi-check-decagram</v-icon
                            >
                            <span class="verify-txt">Verified</span>
                          </div>
                        </transition>
                      </div>

                      <div class="preview-card-wrap">
                        <div class="preview-card-bg-glow"></div>
                        <div class="preview-card pa-5">
                          <!-- Rotating Verification Seal when valid -->
                          <transition name="scale-fade">
                            <div
                              class="stamp-verified-badge"
                              v-if="isAddressValid"
                            >
                              <svg class="stamp-svg" viewBox="0 0 100 100">
                                <path
                                  id="curve"
                                  d="M 20,50 A 30,30 0 1,1 80,50 A 30,30 0 1,1 20,50"
                                  fill="transparent"
                                />
                                <text
                                  fill="#10B981"
                                  font-size="7.5"
                                  font-weight="900"
                                  letter-spacing="0.5"
                                >
                                  <textPath href="#curve">
                                    ★ SECURED SHIPPING ★ VALIDATED
                                  </textPath>
                                </text>
                              </svg>
                              <v-icon color="#10B981" class="stamp-center-icon"
                                >mdi-check-decagram</v-icon
                              >
                            </div>
                          </transition>

                          <div class="preview-field mb-3">
                            <span class="preview-lbl d-block mb-1"
                              >Recipient Name</span
                            >
                            <span
                              :class="[
                                'preview-val',
                                Delivery.ContactName ? 'filled' : 'empty',
                              ]"
                            >
                              {{ Delivery.ContactName || "Pending Name Input" }}
                            </span>
                          </div>

                          <div class="preview-field mb-3">
                            <span class="preview-lbl d-block mb-1"
                              >Mobile Number</span
                            >
                            <span
                              :class="[
                                'preview-val',
                                Delivery.ContactMobile ? 'filled' : 'empty',
                              ]"
                            >
                              {{
                                Delivery.ContactMobile || "Pending Mobile Input"
                              }}
                            </span>
                          </div>

                          <div class="preview-field">
                            <span class="preview-lbl d-block mb-1"
                              >Delivery Address</span
                            >
                            <span
                              :class="[
                                'preview-val address-val-preview',
                                Delivery.Address ? 'filled' : 'empty',
                              ]"
                            >
                              {{
                                Delivery.Address ||
                                "Pending Address & Pincode Input"
                              }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- conversion-focused warning alert -->
                      <transition name="fade">
                        <div
                          class="conversion-alert mt-4 pa-3 d-flex align-center"
                          v-if="!isAddressValid"
                        >
                          <v-icon color="#EF4444" class="mr-2" size="20"
                            >mdi-alert-circle-outline</v-icon
                          >
                          <span class="alert-txt">
                            <span v-if="pincodeMissing">
                              Please include a 6-digit
                              <strong>PINCODE</strong> in the address.
                            </span>
                            <span v-else>
                              Without entering the delivery address including
                              <strong>PINCODE</strong>, the kits cannot be
                              delivered.
                            </span>
                          </span>
                        </div>
                      </transition>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>

          <!-- BENEFITS SECTION -->
          <div class="benefits-container mt-6 slide-up-animation delay-300">
            <h3 class="section-title-alt mb-4">Why Organize JCSAT?</h3>
            <v-row>
              <v-col
                cols="12"
                sm="6"
                v-for="benefit in benefits"
                :key="benefit.title"
                class="mb-4"
              >
                <div class="benefit-card d-flex">
                  <div
                    class="benefit-icon-box mr-4"
                    :style="{
                      color: benefit.color,
                      backgroundColor: benefit.color + '18',
                      borderColor: benefit.color + '33',
                    }"
                  >
                    <v-icon large :color="benefit.color">{{
                      benefit.icon
                    }}</v-icon>
                  </div>
                  <div>
                    <h4 class="benefit-heading">{{ benefit.title }}</h4>
                    <p class="benefit-desc mb-0">{{ benefit.desc }}</p>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-col>

        <!-- RIGHT COLUMN: STICKY DYNAMIC CART & CHECKOUT -->
        <v-col cols="12" lg="4" md="5" class="right-checkout-column">
          <div class="sticky-cart-sidebar" id="checkout-card">
            <v-card
              class="cart-summary-card slide-up-animation delay-300"
              elevation="2"
            >
              <div class="card-glow-overlay"></div>

              <v-card-title class="cart-title py-4 px-6 d-flex align-center">
                <div class="cart-title-icon-wrap mr-3">
                  <v-icon color="white" size="22">mdi-shopping</v-icon>
                </div>
                <span>Order Summary</span>
                <v-spacer></v-spacer>
                <v-chip
                  x-small
                  color="#e3f2fd"
                  text-color="#1565C0"
                  class="font-weight-bold"
                  v-if="selectedProducts.length > 0"
                >
                  {{ selectedProducts.length }} item{{
                    selectedProducts.length > 1 ? "s" : ""
                  }}
                </v-chip>
              </v-card-title>

              <v-divider class="border-light-white"></v-divider>

              <v-card-text class="pa-6">
                <!-- Selected Products List -->
                <div
                  class="selected-items-list mb-6"
                  v-if="selectedProducts.length > 0"
                >
                  <div
                    v-for="(item, idx) in selectedProducts"
                    :key="item.id"
                    class="cart-item-row d-flex justify-space-between align-center py-3 mb-2 fade-in-item"
                    :style="{ animationDelay: idx * 0.08 + 's' }"
                  >
                    <div class="cart-item-info">
                      <span class="cart-item-name font-weight-bold d-block">{{
                        item.title
                      }}</span>
                      <span class="cart-item-qty text-caption"
                        >{{ item.quantity }} Kits × ₹{{ item.price }}</span
                      >
                    </div>
                    <span class="cart-item-subtotal font-weight-bold">
                      ₹{{
                        (item.price * item.quantity).toLocaleString("en-IN")
                      }}
                    </span>
                  </div>

                  <!-- Savings Indicator -->
                  <div
                    class="savings-indicator mt-4 pa-3"
                    v-if="TotalQuantity >= 100"
                  >
                    <div class="d-flex align-center">
                      <v-icon small color="#10B981" class="mr-2"
                        >mdi-gift-outline</v-icon
                      >
                      <span class="savings-text font-weight-bold"
                        >Bulk Order Benefit Activated!</span
                      >
                    </div>
                    <span class="savings-subtext d-block mt-1"
                      >Free digital question banks and teacher guides
                      included.</span
                    >
                  </div>
                </div>

                <!-- Empty State -->
                <div class="cart-empty-state text-center py-8" v-else>
                  <v-icon size="48" color="#CBD5E1" class="mb-3"
                    >mdi-cart-remove</v-icon
                  >
                  <p class="empty-state-title mb-1">Your cart is empty</p>
                  <p class="empty-state-desc mb-0">
                    Select educational kits on the left to begin your order.
                  </p>
                </div>

                <v-divider class="my-4 border-light-white"></v-divider>

                <!-- Calculations -->
                <div class="pricing-breakdown">
                  <div class="pricing-row d-flex justify-space-between py-1">
                    <span class="calc-lbl">Subtotal</span>
                    <span class="calc-val"
                      >₹
                      {{
                        SubTotal.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })
                      }}</span
                    >
                  </div>
                  <div
                    class="pricing-row d-flex justify-space-between py-1"
                    v-if="!inter_state"
                  >
                    <span class="calc-lbl">CGST (9%)</span>
                    <span class="calc-val"
                      >₹
                      {{
                        Cgst.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })
                      }}</span
                    >
                  </div>
                  <div
                    class="pricing-row d-flex justify-space-between py-1"
                    v-if="!inter_state"
                  >
                    <span class="calc-lbl">SGST (9%)</span>
                    <span class="calc-val"
                      >₹
                      {{
                        Sgst.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })
                      }}</span
                    >
                  </div>
                  <div
                    class="pricing-row d-flex justify-space-between py-1"
                    v-if="inter_state"
                  >
                    <span class="calc-lbl">IGST (18%)</span>
                    <span class="calc-val"
                      >₹
                      {{
                        Igst.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })
                      }}</span
                    >
                  </div>

                  <v-divider class="my-3 border-light-white"></v-divider>

                  <div
                    class="pricing-row total-row d-flex justify-space-between align-center pt-1"
                  >
                    <div>
                      <span class="grand-total-label font-weight-black d-block"
                        >Grand Total</span
                      >
                      <span class="gst-inclusive-text text-caption"
                        >18% GST Inclusive</span
                      >
                    </div>
                    <span class="grand-total-val font-weight-black">
                      ₹
                      {{
                        GrandTotal.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      }}
                    </span>
                  </div>
                </div>

                <!-- Checkout CTA -->
                <div class="mt-6">
                  <v-btn
                    :disabled="TotalQuantity < 50"
                    :loading="SubmitFlag"
                    @click.prevent="clickCheckout"
                    block
                    x-large
                    rounded
                    :class="[
                      'secure-checkout-btn text-white',
                      TotalQuantity < 50 ? 'checkout-btn-disabled' : '',
                    ]"
                  >
                    <v-icon left size="20">mdi-shield-lock-outline</v-icon>
                    Proceed To Secure Payment
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <!-- TRUST INDICATORS -->
            <div class="trust-container mt-6">
              <v-row>
                <v-col
                  cols="6"
                  v-for="badge in trustBadges"
                  :key="badge.title"
                  class="pa-2"
                >
                  <div class="trust-badge-card text-center pa-3">
                    <v-icon :color="badge.color" class="mb-2" size="24">{{
                      badge.icon
                    }}</v-icon>
                    <h5 class="trust-badge-title mb-0">{{ badge.title }}</h5>
                    <span class="trust-badge-desc">{{ badge.desc }}</span>
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- TRANSACTION HISTORY -->
      <v-container class="mt-12 px-0 fade-in-animation">
        <v-card class="history-card" elevation="0">
          <!-- Premium History Header -->
          <div class="history-premium-header">
            <div class="history-header-bg-glow"></div>
            <div class="history-header-content d-flex align-center flex-wrap">
              <div class="d-flex align-center mr-auto">
                <div class="history-icon-ring mr-4">
                  <v-icon color="white" size="26">mdi-history</v-icon>
                </div>
                <div>
                  <h3 class="history-title mb-0">Kit Purchase History</h3>
                  <span class="history-subtitle"
                    >Track all your JCSAT kit orders & receipts</span
                  >
                </div>
              </div>
              <div class="history-search-wrap mt-3 mt-sm-0">
                <v-text-field
                  v-model="SearchQuery"
                  prepend-inner-icon="mdi-magnify"
                  label="Search purchases..."
                  single-line
                  hide-details
                  dense
                  solo
                  flat
                  class="history-search-input"
                ></v-text-field>
              </div>
            </div>
            <!-- Stats Strip -->
            <div class="history-stats-strip d-flex flex-wrap mt-4">
              <div class="hist-stat-chip">
                <v-icon x-small color="#60A5FA" class="mr-1"
                  >mdi-package-variant</v-icon
                >
                <span
                  >{{ HistoryData.length }} Order{{
                    HistoryData.length !== 1 ? "s" : ""
                  }}</span
                >
              </div>

              <div class="hist-stat-chip">
                <v-icon x-small color="#FBBF24" class="mr-1"
                  >mdi-clock-outline</v-icon
                >
                <span>Latest Records</span>
              </div>
            </div>
          </div>

          <v-card-text class="pa-4 pa-sm-6">
            <v-data-table
              :headers="TableHeaders"
              :items="HistoryData"
              :search="SearchQuery"
              class="premium-data-table elevation-0"
              :items-per-page="5"
              no-data-text="No previous purchases found."
            >
              <template v-slot:no-data>
                <div class="history-empty-state text-center py-12">
                  <v-icon size="64" color="#CBD5E1" class="mb-4"
                    >mdi-receipt-text-outline</v-icon
                  >
                  <p class="hist-empty-title mb-1">No Purchase History Yet</p>
                  <p class="hist-empty-sub mb-0">
                    Your JCSAT kit orders will appear here after payment.
                  </p>
                </div>
              </template>
              <template v-slot:item.amount="{ item }">
                <span class="amount-cell font-weight-black"
                  >₹{{
                    item.amount.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })
                  }}</span
                >
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="getActiveStatusColor(item.status)"
                  text-color="white"
                  small
                  class="status-chip font-weight-bold"
                >
                  <span class="chip-status-indicator"></span>
                  {{ item.status }}
                </v-chip>
              </template>
              <template v-slot:item.actions="{ item }">
                <div class="d-flex align-center">
                  <!-- Download button -->
                  <v-tooltip bottom v-if="item.ReceiptFilePath">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        small
                        rounded
                        color="#1565C0"
                        dark
                        class="action-download-btn-new px-3"
                        v-bind="attrs"
                        v-on="on"
                        @click.prevent="downloadReceipt(item)"
                        elevation="0"
                      >
                        <v-icon left size="14">mdi-download-outline</v-icon>
                        Receipt
                      </v-btn>
                    </template>
                    <span>Download Receipt PDF</span>
                  </v-tooltip>

                  <span v-if="!item.ReceiptFilePath" class="no-receipt-txt"
                    >Pending</span
                  >
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Premium Warning Dialog -->
        <v-dialog
          v-model="showWarningDialog"
          max-width="480"
          content-class="premium-warning-dialog"
        >
          <v-card class="warning-dialog-card overflow-hidden">
            <div class="warning-header-glow"></div>
            <v-card-text class="text-center pt-8 pb-6 px-6">
              <div class="warning-icon-box mb-4">
                <v-icon color="#EF4444" size="48"
                  >mdi-alert-circle-outline</v-icon
                >
                <div class="warning-pulse"></div>
              </div>
              <h3 class="dialog-warning-title mb-2">
                Delivery Address Required
              </h3>
              <p class="dialog-warning-desc mb-6">
                Delivery address is required before proceeding to payment.
              </p>
              <v-btn
                color="#1565C0"
                dark
                rounded
                large
                block
                @click="showWarningDialog = false"
                class="dialog-confirm-btn"
              >
                I Understand, Let me fill it
              </v-btn>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import Swal from "sweetalert2";
import companyConfig from "@/company_config.json";

export default {
  mixins: [common],
  data() {
    return {
      isHeroCollapsed: false,
      isMemberCollapsed: false,
      isLocationCollapsed: false,
      valid: true,
      LoadingFlag: false,
      SubmitFlag: false,
      PageTitle: "JCSAT Kit Purchase",
      PageDescription:
        "Purchase JCSAT Registration Kits online with secure payment gateways.",

      JciYearName: "",
      GstNumber: "NA",

      StateCode: "",
      StateCodeOptions: [],
      StateCodeOptionsLoading: false,
      StateCodeRules: [(v) => !!v || "State is required"],

      DistrictCode: "",
      DistrictCodeOptions: [],
      DistrictCodeOptionsLoading: false,
      DistrictCodeRules: [(v) => !!v || "District is required"],

      CityCode: "",
      CityCodeOptions: [],
      CityCodeOptionsLoading: false,
      CityCodeRules: [(v) => !!v || "City is required"],
      Member: {
        LomName: "",
        ZoneName: "",
        MemberName: "",
        MembershipId: "",
        PrimaryMobile: "",
        EmailId: "",
        MemberImage: "",
      },

      showWarningDialog: false,
      isNameFocused: false,
      isMobileFocused: false,
      isAddressFocused: false,
      showValidationErrors: false,
      shakeFields: false,
      Delivery: {
        ContactName: "",
        ContactMobile: "",
        Address: "",
      },
      products: [],
      Order: {},

      // Ecommerce Products
      // products: [
      //   {
      //     id: "level1_en",
      //     title: "Level I (English)",
      //     level: "Level I",
      //     lang: "English",
      //     price: 450,
      //     quantity: 0,
      //     minQty: 50,
      //     status: "Available",
      //     classes: "Recommended for Classes 5, 6 & 7",
      //     icon: "mdi-book-open-variant",
      //     color: "#1E88E5",
      //     accent: "#E3F2FD",
      //   },
      //   {
      //     id: "level1_hi",
      //     title: "Level I (Hindi)",
      //     level: "Level I",
      //     lang: "Hindi",
      //     price: 450,
      //     quantity: 0,
      //     minQty: 50,
      //     status: "Available",
      //     classes: "Recommended for Classes 5, 6 & 7",
      //     icon: "mdi-school",
      //     color: "#FF9800",
      //     accent: "#FFF3E0",
      //   },
      //   {
      //     id: "level2_en",
      //     title: "Level II (English)",
      //     level: "Level II",
      //     lang: "English",
      //     price: 500,
      //     quantity: 0,
      //     minQty: 50,
      //     status: "Available",
      //     classes: "Recommended for Classes 8, 9 & 10",
      //     icon: "mdi-brain",
      //     color: "#3F51B5",
      //     accent: "#E8EAF6",
      //   },
      //   {
      //     id: "level2_hi",
      //     title: "Level II (Hindi)",
      //     level: "Level II",
      //     lang: "Hindi",
      //     price: 500,
      //     quantity: 0,
      //     minQty: 50,
      //     status: "Available",
      //     classes: "Recommended for Classes 8, 9 & 10",
      //     icon: "mdi-lightbulb-on",
      //     color: "#E65100",
      //     accent: "#FFE0B2",
      //   },
      //   {
      //     id: "level3_en",
      //     title: "Level III (English)",
      //     level: "Level III",
      //     lang: "English",
      //     price: 550,
      //     quantity: 0,
      //     minQty: 50,
      //     status: "Available",
      //     classes: "Recommended for Classes 11 & 12",
      //     icon: "mdi-trophy-outline",
      //     color: "#1565C0",
      //     accent: "#E1F5FE",
      //   },
      //   {
      //     id: "level3_hi",
      //     title: "Level III (Hindi)",
      //     level: "Level III",
      //     lang: "Hindi",
      //     price: 550,
      //     quantity: 0,
      //     minQty: 50,
      //     status: "Available",
      //     classes: "Recommended for Classes 11 & 12",
      //     icon: "mdi-trophy",
      //     color: "#D84315",
      //     accent: "#FBE9E7",
      //   },
      // ],

      PaymentGateway: 1,

      // Benefits
      benefits: [
        {
          title: "National Ranking & Recognition",
          desc: "Benchmark student aptitude scores against national percentile indicators.",
          icon: "mdi-seal",
          color: "#1565C0",
        },
        {
          title: "Scholarship Allocations",
          desc: "Top academic performers qualify for direct cash scholarship rewards.",
          icon: "mdi-cash-multiple",
          color: "#10B981",
        },
        {
          title: "Official Certifications",
          desc: "Every enrolled participant receives an authorized JCI India achievement cert.",
          icon: "mdi-certificate",
          color: "#F59E0B",
        },
        {
          title: "Comprehensive Student Reports",
          desc: "Includes granular category breakdowns for student diagnostic analysis.",
          icon: "mdi-file-chart",
          color: "#E040FB",
        },
      ],

      // Trust badges
      trustBadges: [
        {
          title: "Secure Checkout",
          desc: "256-Bit SSL Encryption",
          icon: "mdi-shield-check-outline",
          color: "#10B981",
        },
        {
          title: "Instant Assignment",
          desc: "Batch Codes Issued Immediately",
          icon: "mdi-flash-outline",
          color: "#FF9800",
        },
        {
          title: "Official JCI Program",
          desc: "Nationally Accredited Syllabus",
          icon: "mdi-seal",
          color: "#1565C0",
        },
        {
          title: "LO Endorsed",
          desc: "Trusted by Local Chapters",
          icon: "mdi-account-group-outline",
          color: "#673AB7",
        },
      ],

      // Transaction History
      SearchQuery: "",
      TableHeaders: [],
      HistoryData: [],

      // Receipt Preview
      receiptPreviewDialog: false,
      selectedReceiptUrl: null,
    };
  },
  computed: {
    selectedProducts() {
      return this.products.filter((p) => p.quantity >= p.minQty);
    },
    SubTotal() {
      return this.products.reduce((acc, p) => acc + p.price * p.quantity, 0);
    },
    inter_state() {
      const sellerStateId = 21;
      const buyerStateId = parseInt(this.StateCode);

      console.log("sellerStateId =", sellerStateId);
      console.log("buyerStateId =", buyerStateId);
      console.log("inter_state =", buyerStateId !== sellerStateId);

      return buyerStateId > 0 && buyerStateId !== sellerStateId;
    },
    Cgst() {
      return !this.inter_state ? this.SubTotal * 0.09 : 0;
    },

    Sgst() {
      return !this.inter_state ? this.SubTotal * 0.09 : 0;
    },

    Igst() {
      return this.inter_state ? this.SubTotal * 0.18 : 0;
    },
    GrandTotal() {
      return this.SubTotal + this.Cgst + this.Sgst + this.Igst;
    },
    TotalQuantity() {
      return this.products.reduce((acc, p) => acc + p.quantity, 0);
    },
    isNameValid() {
      return (
        !!this.Delivery.ContactName && this.Delivery.ContactName.trim() !== ""
      );
    },
    isMobileValid() {
      return (
        !!this.Delivery.ContactMobile &&
        /^\d{10}$/.test(this.Delivery.ContactMobile.trim())
      );
    },
    isAddressFieldValid() {
      return !!this.Delivery.Address && this.Delivery.Address.trim() !== "";
    },
    hasPincode() {
      return !!this.Delivery.Address && /\b\d{6}\b/.test(this.Delivery.Address);
    },
    isAddressValid() {
      return (
        this.isNameValid &&
        this.isMobileValid &&
        this.isAddressFieldValid &&
        this.hasPincode
      );
    },
    pincodeMissing() {
      if (
        !this.isNameValid ||
        !this.isMobileValid ||
        !this.isAddressFieldValid
      ) {
        return false;
      }
      return !this.hasPincode;
    },
  },
  watch: {
    StateCodeOptions: function () {
      console.log("watch StateCodeOptions");
      this.StateCodeOptionsLoading = false;
    },
    StateCode: function () {
      console.log("watch StateCode");
      this.getDistrictCodeOptions(
        "DistrictCode",
        "DistrictCodeOptions",
        this.StateCode
      );
    },
    DistrictCodeOptions: function () {
      console.log("watch DistrictCodeOptions");
      this.DistrictCodeOptionsLoading = false;
    },
    DistrictCode: function () {
      console.log("watch DistrictCode");
      this.getCityCodeOptions("CityCode", "CityCodeOptions", this.DistrictCode);
    },
    CityCodeOptions: function () {
      console.log("watch CityCodeOptions");
      this.CityCodeOptionsLoading = false;
    },
    CityCode: function () {
      console.log("watch CityCode");
    },
  },
  methods: {
    getStateCodeOptions(source, destination) {
      console.log("getStateCodeOptions called");
      if (source != "" && destination != "") {
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/states/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getDistrictCodeOptions(source, destination, StateCode) {
      console.log("getDistrictCodeOptions called StateCode=" + StateCode);
      if (source != "" && destination != "" && StateCode != "") {
        this.DistrictCodeOptionsLoading = true;
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/district/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          StatesCode: StateCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getCityCodeOptions(source, destination, DistrictCode) {
      console.log("getCityCodeOptions called DistrictCode=" + DistrictCode);
      if (source != "" && destination != "" && DistrictCode != "") {
        this.CityCodeOptionsLoading = true;
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/city/options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          DistrictCode: DistrictCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getInitials(name) {
      if (!name) return "JI";
      const parts = name.split(" ");
      if (parts.length > 1) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    getParticleStyle(n) {
      const delays = ["0s", "1.8s", "3.5s", "0.8s", "4.2s", "2.5s"];
      const sizes = ["48px", "28px", "64px", "20px", "56px", "38px"];
      const tops = ["12%", "60%", "18%", "75%", "32%", "65%"];
      const lefts = ["6%", "82%", "48%", "15%", "91%", "33%"];
      const durations = ["10s", "8s", "12s", "9s", "14s", "11s"];
      return {
        top: tops[n - 1],
        left: lefts[n - 1],
        width: sizes[n - 1],
        height: sizes[n - 1],
        animationDelay: delays[n - 1],
        animationDuration: durations[n - 1],
      };
    },

    getDeliveryParticleStyle(n) {
      const tops = ["12%", "68%", "25%", "85%"];
      const lefts = ["85%", "15%", "5%", "92%"];
      const sizes = ["6px", "8px", "5px", "10px"];
      const delays = ["0s", "1.5s", "0.8s", "2.2s"];
      const durations = ["8s", "12s", "10s", "7s"];
      return {
        top: tops[n - 1],
        left: lefts[n - 1],
        width: sizes[n - 1],
        height: sizes[n - 1],
        animationDelay: delays[n - 1],
        animationDuration: durations[n - 1],
      };
    },

    refreshPageData() {
      console.log("refreshPageData called for JCSAT");
      this.LoadingFlag = true;

      // JCI year from session
      const currentYearName = this.$session.get("CurrentJciYearName");
      this.JciYearName = currentYearName || new Date().getFullYear().toString();

      this.getStateCodeOptions("StateCode", "StateCodeOptions");
      this.searchForm();
    },

    searchForm() {
      console.log("searchForm is called");
      this.LoadingFlag = true;

      // API call to fetch active plans + session member details
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == null || token === undefined ? 0 : token;
      var add_url = server_url + "api/jcsat/plan/lists";

      var upload = { UserInterface: 1 };

      console.log("add_url=" + add_url + ", upload=" + JSON.stringify(upload));

      const thisIns = this;

      // Plan visual config keyed by jcsat_plan_level
      const planVisuals = {
        1: {
          icon: "mdi-book-open-variant",
          color: "#1E88E5",
          accent: "#E3F2FD",
        },
        2: { icon: "mdi-school", color: "#FF9800", accent: "#FFF3E0" },
        3: { icon: "mdi-brain", color: "#3F51B5", accent: "#E8EAF6" },
        4: { icon: "mdi-lightbulb-on", color: "#E65100", accent: "#FFE0B2" },
        5: { icon: "mdi-trophy-outline", color: "#1565C0", accent: "#E1F5FE" },
        6: { icon: "mdi-trophy", color: "#D84315", accent: "#FBE9E7" },
      };

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          console.log("plan/lists response=" + JSON.stringify(response.data));
          thisIns.LoadingFlag = false;

          var flag = response.data.flag;
          var output = response.data.output;
          var records = response.data.records || [];
          var memberDetails = response.data.MemberDetails || {};

          console.log("flag=" + flag + ", output=" + output);
          console.log({ records });
          console.log({ memberDetails });

          if (flag) {
            // Map API records to product cards
            thisIns.products = records.map((plan) => {
              var level = plan.PlanLevel;
              var vis = planVisuals[level] || {
                icon: "mdi-book-outline",
                color: "#1565C0",
                accent: "#E3F2FD",
              };
              return {
                id: "plan_" + plan.PlanId,
                title: plan.LevelLabel || plan.PlanName,
                level:
                  "Level " +
                  (level <= 3
                    ? ["I", "II", "III"][level - 1]
                    : ["I", "II", "III"][level - 4]),
                lang: level <= 3 ? "English" : "Hindi",
                price: plan.PlanPrice,
                quantity: 0,
                minQty: plan.MinQty || 50,
                status: plan.StatusText || "Available",
                classes: plan.PlanClasses || "",
                icon: vis.icon,
                color: vis.color,
                accent: vis.accent,
                planId: plan.PlanId,
              };
            });
          } else {
            thisIns.toast("error", output);
          }

          // Populate member details from session (via API)
          if (memberDetails && memberDetails.MemberId) {
            thisIns.Member = {
              LomName: memberDetails.LomName || "",
              ZoneName: memberDetails.ZoneName || "",
              MemberName: memberDetails.MemberName || "",
              MembershipId: memberDetails.MembershipId || "",
              PrimaryMobile: memberDetails.PrimaryMobile || "",
              EmailId: memberDetails.EmailId || "",
              MemberImage: memberDetails.MemberImage || "",
            };
            thisIns.getStateCodeOptions("StateCode", "StateCodeOptions");
            if (memberDetails.MemberStateId > 0) {
              thisIns.StateCode = parseInt(memberDetails.MemberStateId);
            }
            if (memberDetails.MemberDistrictId > 0) {
              thisIns.DistrictCode = parseInt(memberDetails.MemberDistrictId);
            }
            if (memberDetails.MemberCityId > 0) {
              thisIns.CityCode = parseInt(memberDetails.MemberCityId);
            }
          } else {
            // Fallback from local session
            var user = thisIns.$session.get("user");
            if (user) {
              thisIns.Member = {
                LomName: user.LomName || "",
                ZoneName: user.ZoneName || "",
                MemberName: user.name || user.username || "",
                MembershipId: user.MembershipId || "",
                PrimaryMobile: user.PrimaryMobile || user.mobile || "",
                EmailId: user.EmailId || user.email || "",
                MemberImage: "",
              };
              thisIns.GstNumber = user.GstNumber || "NA";
            }
          }

          // Fetch real purchase history
          var history_url = server_url + "api/jcsat/purchase-history/lists";
          thisIns
            .$http({
              url: history_url,
              method: "POST",
              data: upload,
              headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
              },
            })
            .then(function (histResponse) {
              console.log(
                "purchase-history/lists response=" +
                  JSON.stringify(histResponse.data)
              );
              if (histResponse.data.flag) {
                var records = histResponse.data.records || {};
                thisIns.TableHeaders = records.TableHeader || [];
                thisIns.HistoryData = records.TableData || [];
              } else {
                thisIns.TableHeaders = [];
                thisIns.HistoryData = [];
              }
            })
            .catch(function (histError) {
              console.log("Failed to load purchase history: " + histError);
              thisIns.TableHeaders = [];
              thisIns.HistoryData = [];
            });
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
          thisIns.toast("error", "Failed to load plans. Please refresh.");
        });
    },

    selectProduct(product) {
      if (product.quantity === 0) {
        product.quantity = product.minQty; // Default initialize to minimum quantity (50)
      } else {
        product.quantity = 0; // Toggle selection - set to 0 to remove
      }
    },

    incrementProduct(product) {
      if (product.quantity === 0) {
        product.quantity = product.minQty;
      } else {
        product.quantity++;
      }
    },

    decrementProduct(product) {
      if (product.quantity === product.minQty) {
        product.quantity = 0; // Deselect and remove if decremented at the minimum boundary
      } else if (product.quantity > product.minQty) {
        product.quantity--;
      }
    },

    addQty(product, val) {
      if (product.quantity === 0) {
        product.quantity = product.minQty + val;
      } else {
        product.quantity += val;
      }
    },

    validateProductQty(product) {
      if (product.quantity === 0) return;
      if (product.quantity < product.minQty) {
        product.quantity = product.minQty;
      }
    },

    scrollToCheckout() {
      const element = document.getElementById("checkout-card");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },

    clickCheckout() {
      if (this.TotalQuantity < 50) {
        this.sweetAlert(
          "error",
          "Please select at least one kit option (min qty: 50).",
          false
        );
        return;
      }
      this.processPayment();
    },

    handleCardMouseMove(e) {
      const card = e.currentTarget;
      const box = card.getBoundingClientRect();
      const x = e.clientX - box.left;
      const y = e.clientY - box.top;
      card.style.setProperty("--mouse-x", `${x}px`);
      card.style.setProperty("--mouse-y", `${y}px`);

      const centerX = box.width / 2;
      const centerY = box.height / 2;
      const rotateX = (centerY - y) / 20;
      const rotateY = (x - centerX) / 30;
      card.style.transform = `translateY(-6px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
    },

    handleCardMouseLeave(e) {
      const card = e.currentTarget;
      card.style.transform = `translateY(0px) rotateX(0deg) rotateY(0deg)`;
    },

    processPayment() {
      if (!this.StateCode || !this.DistrictCode || !this.CityCode) {
        this.sweetAlert(
          "error",
          "Please select State, District, and City for the member address.",
          false
        );
        return;
      }
      const validate = this.selectedProducts.length > 0;
      if (!validate || this.GrandTotal <= 0) {
        this.sweetAlert(
          "error",
          "Please select at least one kit option (min qty: 50).",
          false
        );
        return;
      }

      // Final double check for minimum quantity boundary
      const invalidProduct = this.products.find(
        (p) => p.quantity > 0 && p.quantity < p.minQty
      );
      if (invalidProduct) {
        this.sweetAlert(
          "error",
          `${invalidProduct.title} requires a minimum quantity of 50 kits.`,
          false
        );
        return;
      }

      // Generate HTML purchase details for SweetAlert Confirmation
      const productLines = this.selectedProducts
        .map(
          (p) =>
            `<li style="margin-bottom: 6px;"><b>${p.title}</b> × ${
              p.quantity
            } kits (₹${(p.price * p.quantity).toLocaleString()})</li>`
        )
        .join("");

      Swal.fire({
        title: "Confirm Kit Purchase?",
        html: `<div style="text-align: left; font-size: 14px; color: #475569;">
                 <p style="margin-bottom: 12px;">Review details before proceeding to payment gateway:</p>
                 <ul style="padding-left: 20px; margin-bottom: 16px; color: #1e293b;">
                   ${productLines}
                 </ul>
                 <p style="margin-bottom: 6px;"><b>Total quantity:</b> ${
                   this.TotalQuantity
                 } kits</p>
                 <p style="margin-bottom: 12px;"><b>Payment Gateway:</b> Razorpay Checkout</p>
                 <div style="background-color: #f0f7ff; padding: 12px; border-radius: 8px; border-left: 4px solid #1565C0;">
                   <span style="font-size: 13px; color: #1e3a8a; font-weight: bold; display: block;">Grand Total (incl. GST)</span>
                   <span style="font-size: 20px; color: #1565C0; font-weight: 800;">₹${this.GrandTotal.toLocaleString(
                     "en-IN",
                     { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                   )}</span>
                 </div>
               </div>`,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Confirm & Proceed",
        confirmButtonColor: "#1565C0",
        cancelButtonText: "Go Back",
        cancelButtonColor: "#64748b",
      }).then((result) => {
        if (result.isConfirmed) {
          const thisIns = this;
          thisIns.SubmitFlag = true;

          var server_url = companyConfig.apiURL;
          var token = this.$session.get("token");
          token = token == null || token === undefined ? 0 : token;
          var add_url = server_url + "api/jcsat/order/create";

          var upload = {
            UserInterface: 1,
            Products: this.selectedProducts.map((p) => ({
              planId: p.planId,
              quantity: p.quantity,
              price: p.price,
            })),
            MobileNo: this.Member.PrimaryMobile,
            EmailId: this.Member.EmailId,
            LoState: this.StateCode,
            LoDistrict: this.DistrictCode,
            LoCity: this.CityCode,
          };

          this.$http({
            url: add_url,
            method: "POST",
            data: upload,
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          })
            .then(function (response) {
              thisIns.SubmitFlag = false;
              var flag = response.data.flag;
              var output = response.data.output;
              var records = response.data.records;
              if (flag == 1) {
                thisIns.Order = records.Order;
                thisIns.makePayment();
              } else {
                thisIns.sweetAlert("error", output, false);
              }
            })
            .catch(function (error) {
              console.log("error=" + error);
              thisIns.SubmitFlag = false;
              thisIns.sweetAlert(
                "error",
                "Some problem occurred while processing your request.",
                false
              );
            });
        }
      });
    },

    makePayment() {
      console.log("makePayment");
      var order = this.Order;
      console.log({ order });
      const thisIns = this;
      var rzp1 = new Razorpay({
        key: order.rzp_key,
        account_id: order.rzp_account_id,
        order_id: order.rzp_order_id,
        amount: order.rzp_amount,
        name: order.rzp_name,
        currency: "INR",
        description: order.rzp_description,
        image: order.rzp_image,
        theme: {
          color: order.rzp_color,
        },
        prefill: {
          name: order.customer_name,
          email: order.customer_email_id,
          contact: order.customer_mobile_no,
        },
        notes: {
          address: "",
        },
        redirect: false,
        handler: function (response) {
          console.log("razorpay_payment_id=" + response.razorpay_payment_id);
          thisIns.SubmitFlag = true;
          thisIns.pageRedirect({}, "/order/success");
        },
      });
      rzp1.open();
    },

    downloadReceipt(item) {
      if (item.ReceiptFilePath) {
        window.open(item.ReceiptFilePath, "_blank");
      } else {
        this.sweetAlert("error", "Receipt file not found.", false);
      }
    },

    getActiveStatusColor(status) {
      if (status === "Approved" || status === "Success") {
        return "#10B981"; // Emerald Green
      } else if (status === "Pending") {
        return "#F59E0B"; // Amber
      }
      return "#6B7280"; // Grey
    },
    getSelectedLocationText() {
      if (!this.StateCodeOptions || !this.StateCode)
        return "No Location Selected";
      const stateObj = this.StateCodeOptions.find(
        (o) => o && (o.value == this.StateCode || o == this.StateCode)
      );
      const distObj = this.DistrictCodeOptions.find(
        (o) => o && (o.value == this.DistrictCode || o == this.DistrictCode)
      );
      const cityObj = this.CityCodeOptions.find(
        (o) => o && (o.value == this.CityCode || o == this.CityCode)
      );

      const stateName = stateObj
        ? stateObj.text ||
          stateObj.label ||
          (typeof stateObj === "object" ? stateObj.text : stateObj)
        : "";
      const distName = distObj
        ? distObj.text ||
          distObj.label ||
          (typeof distObj === "object" ? distObj.text : distObj)
        : "";
      const cityName = cityObj
        ? cityObj.text ||
          cityObj.label ||
          (typeof cityObj === "object" ? cityObj.text : cityObj)
        : "";

      const parts = [cityName, distName, stateName].filter(Boolean);
      return parts.length > 0 ? parts.join(", ") : "No Location Selected";
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

.premium-purchase-experience {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background-color: #f8fafc;
  min-height: 100vh;
  position: relative;
  overflow: visible;
  padding-bottom: 80px;

  // --- Dynamic Animated Backgrounds ---
  .animated-mesh-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 600px;
    background: linear-gradient(185deg, #e0f2fe 0%, #eef2f6 50%, #f8fafc 100%);
    z-index: 0;
  }

  .background-decorations {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;

    .blur-blob {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.4;
      mix-blend-mode: multiply;
      animation: morphBlob 12s infinite alternate ease-in-out;
    }

    .blob-1 {
      top: -10%;
      right: 5%;
      width: 400px;
      height: 400px;
      background-color: #bfdbfe;
    }

    .blob-2 {
      top: 30%;
      left: -10%;
      width: 350px;
      height: 350px;
      background-color: #ddd6fe;
      animation-delay: -3s;
    }

    .blob-3 {
      bottom: 10%;
      right: 10%;
      width: 450px;
      height: 450px;
      background-color: #e0f2fe;
      animation-delay: -6s;
    }
  }

  @keyframes morphBlob {
    0% {
      transform: translate(0, 0) scale(1) rotate(0deg);
      border-radius: 50%;
    }
    33% {
      transform: translate(30px, -40px) scale(1.1) rotate(60deg);
      border-radius: 40% 60% 50% 50%;
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9) rotate(-40deg);
      border-radius: 60% 40% 30% 70%;
    }
    100% {
      transform: translate(0, 0) scale(1) rotate(0deg);
      border-radius: 50%;
    }
  }

  .max-width-container {
    max-width: 100% !important;
    position: relative;
    z-index: 1;
  }

  // --- Header back navigation row ---
  .nav-header-row {
    .back-btn {
      text-transform: none;
      letter-spacing: normal;
      font-size: 15px;
      padding: 0 16px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(226, 232, 240, 0.8);
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(21, 101, 192, 0.05);
        transform: translateX(-3px);
      }
    }

    .jci-year-pill {
      background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%);
      color: white;
      padding: 6px 14px;
      border-radius: 20px;
      letter-spacing: 0.5px;
      box-shadow: 0 4px 12px rgba(21, 101, 192, 0.2);
    }
  }

  // --- HERO BANNER ---
  .hero-banner {
    position: relative;
    background: linear-gradient(
      135deg,
      #1e3a8a 0%,
      #1565c0 50%,
      #0097d7 100%
    ) !important;
    background-size: 400% 400% !important;
    animation: gradientShift 15s ease infinite !important;
    border-radius: 28px;
    padding: 36px 44px;
    min-height: 200px;
    overflow: hidden;
    color: #ffffff;
    box-shadow: 0 25px 50px rgba(21, 101, 192, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.12);
    margin-bottom: 24px;
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(
        circle at 80% 20%,
        rgba(59, 130, 246, 0.15) 0%,
        transparent 60%
      );
      pointer-events: none;
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 28px;
      padding: 2px;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.25),
        rgba(255, 255, 255, 0.05) 50%,
        rgba(255, 255, 255, 0.25)
      );
      -webkit-mask: linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
      z-index: 2;
    }

    .hero-collapse-btn {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.15);
      border: 1px solid rgba(255, 255, 255, 0.25);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: scale(1.1);
      }
    }

    &.hero-banner-collapsed {
      min-height: 54px !important;
      padding: 12px 24px !important;
      border-radius: 16px !important;
      margin-bottom: 16px !important;
      cursor: pointer;
      box-shadow: 0 10px 25px rgba(21, 101, 192, 0.08) !important;

      &::before {
        border-radius: 16px;
      }

      &:hover {
        background: linear-gradient(
          135deg,
          #1e3a8a 0%,
          #1e40af 50%,
          #1565c0 100%
        ) !important;
      }
    }

    .hero-collapsed-content {
      .collapsed-title {
        font-size: 16px;
        color: #ffffff;
        letter-spacing: -0.2px;
      }

      .collapsed-subtitle {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.8);
        font-weight: 500;
      }

      .expand-prompt {
        color: rgba(255, 255, 255, 0.9);
        animation: pulseGreenText 1.5s infinite;
      }
    }

    .badge-hero {
      display: inline-flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 20px;
      padding: 6px 14px;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }

    .hero-title {
      font-size: 36px;
      font-weight: 900;
      line-height: 1.15;
      letter-spacing: -1px;
      white-space: nowrap;

      @media (max-width: 960px) {
        white-space: normal;
      }
      @media (max-width: 600px) {
        font-size: 26px;
      }
    }

    .text-gradient-sky {
      background: linear-gradient(90deg, #ffd54f 0%, #fff176 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      font-weight: 850;
    }

    .hero-subtitle {
      font-size: 16px;
      line-height: 1.6;
      opacity: 0.85;
      margin-top: 14px;
      max-width: 800px;
      font-weight: 400;
    }

    .stats-row {
      max-width: 800px;
    }

    .stat-bubble-card {
      background: rgba(255, 255, 255, 0.06);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      padding: 12px 14px;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.12);
        transform: translateY(-2px);
      }

      .stat-val {
        font-size: 18px;
        letter-spacing: -0.5px;
      }

      .stat-lbl {
        font-size: 11px;
        opacity: 0.7;
        font-weight: 600;
        text-transform: uppercase;
        display: block;
      }
    }

    .illustration-container {
      position: relative;
      width: 150px;
      height: 150px;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: floatEffect3D 6s infinite ease-in-out;

      .hero-svg-art {
        width: 100%;
        height: 100%;
        filter: drop-shadow(0 10px 20px rgba(59, 130, 246, 0.3));
      }
    }
  }

  // --- MEMBER PROFILE glass card ---
  .member-profile-card {
    background: rgba(255, 255, 255, 0.8) !important;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.5) !important;
    border-radius: 28px !important;
    padding: 36px !important;

    .field-label-custom {
      font-size: 13px !important;
      font-weight: 600 !important;
      color: #475569 !important;
      margin-bottom: 8px !important;
      display: block;
    }
    position: relative;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04) !important;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, #1565c0 0%, #60a5fa 50%, #818cf8 100%);
    }

    .profile-header-badge {
      position: absolute;
      top: 18px;
      right: 18px;
      background: rgba(16, 185, 129, 0.08);
      color: #10b981;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 750;
      letter-spacing: 0.2px;
      text-transform: uppercase;
      border: 1px solid rgba(16, 185, 129, 0.15);

      @media (max-width: 600px) {
        position: relative;
        top: 0;
        right: 0;
        display: inline-flex !important;
        margin-bottom: 16px;
      }
    }

    .badge-status-dot {
      width: 6px;
      height: 6px;
      background-color: #10b981;
      border-radius: 50%;
      display: inline-block;
      margin-right: 6px;
      animation: pulseGreen 1.8s infinite;
    }

    .avatar-glow {
      position: relative;
      width: 100px;
      height: 100px;
      border-radius: 50%;
      background: linear-gradient(135deg, #1565c0 0%, #60a5fa 100%);
      padding: 3px;
      box-shadow: 0 8px 20px rgba(21, 101, 192, 0.2);

      .profile-avatar-initials {
        background: #ffffff;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 800;
        font-size: 30px;
        color: #1565c0;
        overflow: hidden;

        .profile-avatar-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 50%;
        }
      }
    }

    .member-title-name {
      font-size: 28px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.5px;
    }

    .member-subtitle-id {
      font-size: 13px;
      font-weight: 600;
      color: #64748b;

      .id-pill {
        background: #f1f5f9;
        padding: 3px 8px;
        border-radius: 6px;
      }
    }

    .member-field-item {
      display: flex;
      align-items: center;
      background: rgba(248, 250, 252, 0.6);
      border: 1px solid rgba(226, 232, 240, 0.6);
      padding: 16px 20px;
      border-radius: 16px;
      transition: all 0.3s ease;

      &:hover {
        background: #ffffff;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.02);
        border-color: rgba(21, 101, 192, 0.15);
      }

      .field-lbl {
        font-size: 12px;
        font-weight: 700;
        color: #64748b;
        text-transform: uppercase;
        display: block;
        margin-bottom: 2px;
      }

      .field-val {
        font-size: 16px;
        font-weight: 700;
        color: #1e293b;
        display: block;
      }
    }
  }

  // --- PRODUCT SECTIONS ---
  .section-main-heading {
    font-size: 26px;
    font-weight: 850;
    color: #0f172a;
    letter-spacing: -0.5px;
  }

  .section-sub-heading {
    font-size: 14px;
    color: #64748b;
  }

  .product-card {
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.8);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
    border-radius: 28px;
    padding: 32px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    cursor: pointer;

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 35px rgba(21, 101, 192, 0.08);
      border-color: rgba(21, 101, 192, 0.25);
    }

    &.product-card-selected {
      border: 2px solid #1565c0;
      background: linear-gradient(180deg, #ffffff 0%, #f0f7ff 100%);
      box-shadow: 0 20px 40px rgba(21, 101, 192, 0.12);
      animation: selectedGlow 3s infinite ease-in-out;
    }

    .selected-checkmark-badge {
      position: absolute;
      top: 0;
      right: 0;
      background: #1565c0;
      padding: 6px 12px 10px 10px;
      border-bottom-left-radius: 24px;
    }

    .product-illustration-wrapper {
      height: 140px;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      position: relative;

      .premium-icon-badge-container {
        width: 82px;
        height: 82px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #ffffff;
        position: relative;
        z-index: 2;
        transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        border: 3px solid currentColor;

        &::after {
          content: "";
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          border-radius: 50%;
          border: 1.5px dashed currentColor;
          opacity: 0.35;
          animation: rotateRing 20s infinite linear;
          transform-origin: center;
        }

        .premium-badge-icon {
          transition: all 0.3s ease;
        }
      }
    }

    &:hover {
      .premium-icon-badge-container {
        transform: scale(1.1) rotate(5deg);
      }
    }

    .product-medium-badge {
      font-size: 13px;
      font-weight: 750;
      letter-spacing: 0.2px;
      text-transform: uppercase;
      padding: 4px 10px;
      border-radius: 10px;
    }

    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      display: inline-block;
      margin-right: 4px;

      &.green-dot {
        background-color: #10b981;
        box-shadow: 0 0 8px #10b981;
      }
    }

    .product-title {
      font-size: 24px;
      font-weight: 800;
      color: #0f172a;
      margin: 8px 0 2px 0;
    }

    .product-classes {
      font-size: 14px !important;
      color: #64748b;
      font-weight: 500;
    }

    .product-price-label {
      font-size: 13px;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
      margin-bottom: 2px;
    }

    .product-price-val {
      font-size: 28px;
      font-weight: 900;
      color: #0f172a;
    }

    .qty-display-number {
      font-size: 18px;
      font-weight: 800;
      color: #0f172a;
      min-width: 24px;
      text-align: center;
    }

    .qty-step-btn {
      border: 1.5px solid rgba(21, 101, 192, 0.2);
      background: #ffffff;
      &:hover {
        background: #f0f7ff;
        border-color: #1565c0;
      }
    }

    .qty-stepper {
      background: #f8fafc;
      border-radius: 30px;
      padding: 3px;
      border: 1px solid rgba(226, 232, 240, 0.8);
    }

    .border-top-dash {
      border-top: 1px dashed rgba(226, 232, 240, 0.8);
    }

    .preset-title {
      color: #1565c0;
      font-weight: 800;
      font-size: 15px !important;
      letter-spacing: 0.2px;
    }

    .preset-btn {
      font-size: 16px !important;
      height: 36px !important;
      padding: 0 10px !important;
      border-color: rgba(21, 101, 192, 0.3);
      font-weight: 800 !important;
      letter-spacing: 0.5px;

      &:hover {
        background: #1565c0 !important;
        color: white !important;
        box-shadow: 0 4px 12px rgba(21, 101, 192, 0.25);
      }
    }

    .add-plan-btn-text {
      font-size: 16px !important;
      font-weight: 800;
      letter-spacing: 0.3px;
    }

    .qty-type-input {
      width: 72px;
      text-align: center;
      font-size: 18px;
      font-weight: 800;
      color: #0f172a;
      border: 2px solid rgba(21, 101, 192, 0.25);
      border-radius: 12px;
      padding: 4px 8px;
      outline: none;
      background: #f8fafc;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
      -moz-appearance: textfield;

      &:focus {
        border-color: #1565c0;
        box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.12);
        background: #ffffff;
      }

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        opacity: 1;
        height: 28px;
      }
    }
  } // end .product-card

  @keyframes selectedGlow {
    0%,
    100% {
      border-color: #1565c0;
      box-shadow: 0 20px 40px rgba(21, 101, 192, 0.08);
    }
    50% {
      border-color: #3b82f6;
      box-shadow: 0 20px 45px rgba(21, 101, 192, 0.16);
    }
  }

  // --- STICKY DYNAMIC CART ---
  .sticky-cart-sidebar {
    position: sticky;
    top: 100px;
    z-index: 10;
  }

  .cart-summary-card {
    background: linear-gradient(
      160deg,
      #f0f7ff 0%,
      #ffffff 55%,
      #f8fafc 100%
    ) !important;
    backdrop-filter: blur(24px);
    border: 1.5px solid rgba(21, 101, 192, 0.1) !important;
    border-radius: 28px !important;
    overflow: hidden;
    position: relative;
    box-shadow: 0 4px 6px rgba(21, 101, 192, 0.04),
      0 20px 40px rgba(15, 23, 42, 0.08) !important;
    transition: box-shadow 0.3s ease;

    &:hover {
      box-shadow: 0 4px 6px rgba(21, 101, 192, 0.08),
        0 24px 48px rgba(21, 101, 192, 0.12) !important;
    }

    .card-glow-overlay {
      position: absolute;
      top: -20%;
      right: -20%;
      width: 240px;
      height: 240px;
      background: radial-gradient(
        circle,
        rgba(21, 101, 192, 0.1) 0%,
        rgba(96, 165, 250, 0.06) 40%,
        transparent 70%
      );
      pointer-events: none;
      animation: pulseGlow 4s ease-in-out infinite;
    }

    .cart-title {
      font-size: 22px;
      font-weight: 800;
      color: #0f172a;
      background: linear-gradient(
        135deg,
        #1e3a8a 0%,
        #1565c0 50%,
        #0097d7 100%
      );
      border-radius: 27px 27px 0 0;
      color: #ffffff !important;
      position: relative;
      overflow: hidden;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: -100%;
        width: 60%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.15) 50%,
          transparent 100%
        );
        animation: shimmerCart 3.5s ease-in-out infinite;
      }

      .cart-title-icon-wrap {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.18);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(255, 255, 255, 0.25);
      }
    }

    .cart-item-row {
      border-bottom: 1px solid rgba(21, 101, 192, 0.06);
      border-left: 3px solid transparent;
      padding-left: 10px !important;
      border-radius: 0 8px 8px 0;
      transition: all 0.25s ease;

      &:hover {
        border-left-color: #1565c0;
        background: rgba(21, 101, 192, 0.03);
        border-radius: 8px;
      }

      .cart-item-name {
        font-size: 14px;
        color: #1e293b;
      }
      .cart-item-qty {
        color: #64748b;
        font-weight: 500;
      }
      .cart-item-subtotal {
        font-size: 14px;
        color: #1565c0;
        font-weight: 800;
      }
    }

    .savings-indicator {
      background: rgba(16, 185, 129, 0.06);
      border: 1px solid rgba(16, 185, 129, 0.12);
      border-radius: 12px;

      .savings-text {
        color: #059669;
        font-size: 12px;
      }
      .savings-subtext {
        font-size: 10.5px;
        color: #047857;
        font-weight: 500;
      }
    }

    .empty-state-title {
      font-size: 15px;
      font-weight: 700;
      color: #475569;
    }
    .empty-state-desc {
      font-size: 12px;
      color: #94a3b8;
    }

    .calc-lbl {
      color: #64748b;
      font-size: 16px;
      font-weight: 600;
    }
    .calc-val {
      color: #1e293b;
      font-size: 16px;
      font-weight: 700;
    }

    .total-row {
      background: linear-gradient(135deg, #e3f2fd 0%, #e8f5fe 100%);
      border-radius: 16px;
      padding: 14px 16px;
      border: 1px solid rgba(21, 101, 192, 0.1);
      animation: totalRowPulse 3s ease-in-out infinite;

      .grand-total-label {
        font-size: 20px;
        color: #0f172a;
        font-weight: 800;
      }
      .grand-total-val {
        font-size: 30px;
        color: #1565c0;
        font-weight: 900;
        letter-spacing: -1px;
        text-shadow: 0 2px 8px rgba(21, 101, 192, 0.2);
      }
      .gst-inclusive-text {
        color: #94a3b8;
        font-weight: 500;
        font-size: 13px;
      }
    }

    .gateway-title {
      font-size: 12px;
      font-weight: 750;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .gateway-option-card {
      border: 1.5px solid rgba(226, 232, 240, 0.8);
      border-radius: 16px;
      background: #ffffff;
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover {
        border-color: rgba(21, 101, 192, 0.2);
        background-color: #fafbfd;
      }

      &.gateway-selected {
        border-color: #1565c0;
        background-color: #f0f7ff;
        box-shadow: 0 4px 15px rgba(21, 101, 192, 0.05);
      }

      .gateway-name {
        font-size: 13.5px;
        color: #1e293b;
      }
      .gateway-sub {
        color: #64748b;
        font-weight: 500;
        line-height: 1;
      }
    }

    .secure-checkout-btn {
      background: linear-gradient(
        135deg,
        #1565c0 0%,
        #1d4ed8 50%,
        #3b82f6 100%
      ) !important;
      background-size: 200% auto;
      text-transform: none;
      letter-spacing: normal;
      font-size: 16px !important;
      font-weight: 800;
      height: 54px !important;
      box-shadow: 0 8px 25px rgba(21, 101, 192, 0.25) !important;
      transition: all 0.3s ease;
      animation: shimmerBtn 3s infinite linear;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(21, 101, 192, 0.35) !important;
      }

      &.v-btn--disabled {
        background: #e2e8f0 !important;
        color: #94a3b8 !important;
        box-shadow: none !important;
      }
    }
  }

  @keyframes shimmerBtn {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  // --- BENEFITS SECTION STYLES ---
  .section-title-alt {
    font-size: 20px;
    font-weight: 850;
    color: #0f172a;
    letter-spacing: -0.5px;
  }

  .benefit-card {
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.8);
    border-radius: 20px;
    padding: 20px;
    height: 100%;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.02);
      transform: translateY(-2px);
    }

    .benefit-icon-box {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      border: 1.5px solid transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: transform 0.3s ease, box-shadow 0.3s ease;

      &:hover {
        transform: scale(1.1);
      }
    }

    .benefit-heading {
      font-size: 15px;
      font-weight: 800;
      color: #1e293b;
      margin-bottom: 4px;
    }

    .benefit-desc {
      font-size: 13px;
      color: #64748b;
      line-height: 1.5;
    }
  }

  // --- TRUST BADGES ---
  .trust-badge-card {
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(226, 232, 240, 0.8);
    border-radius: 16px;
    height: 100%;
    transition: all 0.3s ease;

    &:hover {
      background: #ffffff;
      transform: scale(1.02);
    }

    .trust-badge-title {
      font-size: 11.5px;
      font-weight: 800;
      color: #1e293b;
    }
    .trust-badge-desc {
      font-size: 9.5px;
      color: #64748b;
      display: block;
      font-weight: 500;
    }
  }

  // --- HISTORY CARD & DATA TABLE ---
  .history-card {
    background: #ffffff !important;
    border: 1px solid rgba(226, 232, 240, 0.8) !important;
    border-radius: 28px !important;
    overflow: hidden;
    box-shadow: 0 8px 30px rgba(15, 23, 42, 0.06) !important;

    .history-premium-header {
      position: relative;
      background: linear-gradient(
        135deg,
        #1e3a8a 0%,
        #1565c0 50%,
        #0097d7 100%
      );
      padding: 28px 32px 24px 32px;
      overflow: hidden;

      @media (max-width: 600px) {
        padding: 20px 16px 18px 16px;
      }

      .history-header-bg-glow {
        position: absolute;
        top: -40%;
        right: -10%;
        width: 300px;
        height: 300px;
        border-radius: 50%;
        background: radial-gradient(
          circle,
          rgba(255, 255, 255, 0.08) 0%,
          transparent 70%
        );
        pointer-events: none;
      }

      .history-header-content {
        position: relative;
        z-index: 2;
      }

      .history-icon-ring {
        width: 52px;
        height: 52px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.15);
        border: 2px solid rgba(255, 255, 255, 0.25);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        backdrop-filter: blur(8px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      }

      .history-title {
        font-size: 22px;
        font-weight: 850;
        color: #ffffff;
        letter-spacing: -0.3px;
      }

      .history-subtitle {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.7);
        font-weight: 500;
        display: block;
        margin-top: 2px;
      }

      .history-search-wrap {
        .history-search-input {
          width: 280px;
          border-radius: 12px !important;
          background: rgba(255, 255, 255, 0.15) !important;
          backdrop-filter: blur(10px);
          border: 1.5px solid rgba(255, 255, 255, 0.25) !important;
          color: white;

          .v-input__slot {
            background: transparent !important;
            color: white !important;
          }

          .v-label,
          input,
          .v-icon {
            color: rgba(255, 255, 255, 0.85) !important;
          }

          @media (max-width: 600px) {
            width: 100%;
          }
        }
      }

      .history-stats-strip {
        position: relative;
        z-index: 2;
        gap: 10px;

        .hist-stat-chip {
          display: inline-flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          padding: 5px 14px;
          font-size: 12px;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.9);
          letter-spacing: 0.2px;
          backdrop-filter: blur(6px);
          transition: all 0.2s ease;

          &:hover {
            background: rgba(255, 255, 255, 0.2);
          }
        }
      }
    }

    .history-empty-state {
      .hist-empty-title {
        font-size: 18px;
        font-weight: 700;
        color: #475569;
      }
      .hist-empty-sub {
        font-size: 14px;
        color: #94a3b8;
      }
    }
  }

  .premium-data-table {
    tr th {
      color: #475569 !important;
      font-weight: 750 !important;
      font-size: 14px !important;
      background-color: #f8fafc !important;
      border-bottom: 2px solid #e2e8f0 !important;
    }

    tr td {
      font-size: 14.5px !important;
      color: #1e293b;
      padding: 16px 8px !important;
      border-bottom: 1px solid #f1f5f9 !important;
    }

    .amount-cell {
      color: #1565c0;
      font-size: 15px;
    }

    .status-chip {
      font-size: 12px !important;
      padding: 0 12px !important;
      height: 26px !important;
      border-radius: 8px;
      letter-spacing: 0.2px;

      .chip-status-indicator {
        width: 6px;
        height: 6px;
        background-color: white;
        border-radius: 50%;
        margin-right: 6px;
        display: inline-block;
        animation: pulseGreen 2s infinite;
      }
    }

    .action-download-btn-new {
      font-size: 12px;
      font-weight: 700;
      text-transform: none;
      letter-spacing: 0.2px;
      transition: all 0.25s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(21, 101, 192, 0.25) !important;
      }
    }

    .no-receipt-txt {
      font-size: 12px;
      color: #94a3b8;
      font-weight: 600;
      font-style: italic;
      background: #f8fafc;
      padding: 3px 10px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
    }
  }
}

// --- MOBILE STICKY FOOTER ---
.mobile-sticky-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%);
  box-shadow: 0 -8px 25px rgba(15, 23, 42, 0.15);
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  .mobile-lbl {
    font-size: 9px;
    color: #94a3b8;
    font-weight: 750;
    letter-spacing: 0.5px;
  }

  .mobile-val {
    font-size: 14px;
    color: #ffffff;
  }

  .mobile-price {
    font-size: 16px;
    color: #60a5fa;
  }

  .checkout-trigger-btn {
    text-transform: none;
    letter-spacing: normal;
    height: 38px !important;
    color: #1e3a8a !important;
    font-size: 13px !important;
  }
}

// --- KEYFRAME ANIMATIONS ---
@keyframes pulseGreen {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes pulseGreenText {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@keyframes rotateRing {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spin-animation {
  animation: rotateRing 25s infinite linear;
  transform-origin: center;
}

@keyframes floatEffect {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.floating-star {
  animation: floatEffect 3s infinite ease-in-out;
}

@keyframes pulseArt {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}

.pulse-animation {
  animation: pulseArt 4s infinite ease-in-out;
}

// Entrance animations
@keyframes slideUpIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-up-animation {
  animation: slideUpIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.delay-100 {
  animation-delay: 0.1s;
}
.delay-200 {
  animation-delay: 0.2s;
}
.delay-300 {
  animation-delay: 0.3s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-in-animation {
  animation: fadeIn 0.8s ease forwards;
}

.fade-in-item {
  animation: fadeIn 0.4s ease forwards;
}

// --- FLOATING 3D BUBBLES ---
@keyframes float-bubble-3d {
  0% {
    transform: translateY(0) translateX(0) scale(1) rotate(0deg);
    opacity: 0.25;
  }
  33% {
    transform: translateY(-30px) translateX(12px) scale(1.08) rotate(120deg);
    opacity: 0.55;
  }
  66% {
    transform: translateY(-15px) translateX(-12px) scale(0.92) rotate(240deg);
    opacity: 0.45;
  }
  100% {
    transform: translateY(0) translateX(0) scale(1) rotate(360deg);
    opacity: 0.25;
  }
}

.particle {
  position: absolute;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 255, 255, 0.48) 0%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0.28) 75%,
    rgba(255, 255, 255, 0) 100%
  );
  border: 0.5px solid rgba(255, 255, 255, 0.4);
  box-shadow: inset -3px -3px 8px rgba(0, 0, 0, 0.08),
    inset 3px 3px 6px rgba(255, 255, 255, 0.35), 0 10px 20px rgba(0, 0, 0, 0.06);
  border-radius: 50%;
  pointer-events: none;
  z-index: 1;
  animation: float-bubble-3d 10s ease-in-out infinite;

  &::before {
    content: "";
    position: absolute;
    top: 15%;
    left: 15%;
    width: 25%;
    height: 25%;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.75);
    filter: blur(0.2px);
  }
}

// --- CART SUMMARY ANIMATIONS ---
@keyframes shimmerCart {
  0% {
    left: -100%;
  }
  60% {
    left: 160%;
  }
  100% {
    left: 160%;
  }
}

@keyframes pulseGlow {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

@keyframes totalRowPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(21, 101, 192, 0);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(21, 101, 192, 0.06);
  }
}

/* --- PREMIUM DELIVERY SECTION STYLES --- */
.delivery-section {
  position: relative;
  z-index: 2;

  .delivery-glass-card {
    background: rgba(255, 255, 255, 0.75) !important;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.5) !important;
    border-radius: 24px !important;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.03) !important;
    position: relative;
    transition: box-shadow 0.6s cubic-bezier(0.16, 1, 0.3, 1),
      border-color 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.1s ease-out !important;
    transform-style: preserve-3d;
    perspective: 1000px;

    &:hover {
      box-shadow: 0 35px 70px rgba(21, 101, 192, 0.12) !important;
      border-color: rgba(21, 101, 192, 0.25) !important;
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
        800px circle at var(--mouse-x, 0) var(--mouse-y, 0),
        rgba(21, 101, 192, 0.04),
        transparent 40%
      );
      z-index: 1;
      pointer-events: none;
      border-radius: 24px;
    }

    .delivery-premium-header {
      position: relative;
      background: linear-gradient(
        135deg,
        #0f4cff 0%,
        #1565c0 35%,
        #42a5f5 100%
      );
      border-top-left-radius: 23px;
      border-top-right-radius: 23px;
      overflow: hidden;
      padding: 24px 32px 20px 32px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      z-index: 5;

      @media (max-width: 600px) {
        padding: 16px 20px 16px 20px;
        gap: 12px;
      }

      /* BACKGROUND EFFECTS */
      .header-particle-track {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
        z-index: 1;

        .header-particle {
          position: absolute;
          width: 6px;
          height: 6px;
          background: rgba(255, 255, 255, 0.4);
          border-radius: 50%;
          animation: hParticleFloat 8s infinite ease-in-out;

          &.hp-1 {
            top: 20%;
            left: 15%;
            animation-delay: 0s;
            width: 4px;
            height: 4px;
          }
          &.hp-2 {
            top: 60%;
            left: 45%;
            animation-delay: 2s;
            width: 6px;
            height: 6px;
          }
          &.hp-3 {
            top: 30%;
            left: 75%;
            animation-delay: 4s;
            width: 5px;
            height: 5px;
          }
          &.hp-4 {
            top: 75%;
            left: 85%;
            animation-delay: 1s;
            width: 3px;
            height: 3px;
          }
          &.hp-5 {
            top: 15%;
            left: 55%;
            animation-delay: 3s;
            width: 5px;
            height: 5px;
          }
          &.hp-6 {
            top: 80%;
            left: 25%;
            animation-delay: 5s;
            width: 7px;
            height: 7px;
          }
        }
      }

      .header-light-streaks {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
        z-index: 1;
        overflow: hidden;

        .streak {
          position: absolute;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.15),
            transparent
          );
          height: 100%;
          width: 200px;
          transform: skewX(-30deg);
          animation: hStreakMove 12s infinite linear;

          &.streak-1 {
            left: -300px;
            top: 0;
            animation-duration: 8s;
          }
          &.streak-2 {
            left: -300px;
            top: 0;
            animation-duration: 14s;
            animation-delay: 3s;
          }
        }
      }

      .header-glass-bubbles {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
        z-index: 1;

        .bubble {
          position: absolute;
          border-radius: 50%;
          background: radial-gradient(
            circle at 30% 30%,
            rgba(255, 255, 255, 0.15),
            rgba(255, 255, 255, 0.02) 70%
          );
          border: 1px solid rgba(255, 255, 255, 0.25);
          box-shadow: inset 0 0 10px rgba(255, 255, 255, 0.1);
          animation: hBubbleFloat 10s infinite ease-in-out;

          &.bubble-1 {
            width: 30px;
            height: 30px;
            left: 10%;
            bottom: -15px;
            animation-duration: 9s;
          }
          &.bubble-2 {
            width: 20px;
            height: 20px;
            right: 30%;
            top: -10px;
            animation-duration: 12s;
            animation-delay: 2s;
          }
          &.bubble-3 {
            width: 40px;
            height: 40px;
            right: 15%;
            bottom: -20px;
            animation-duration: 15s;
            animation-delay: 1s;
          }
        }
      }

      .header-glowing-circles {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
        z-index: 0;

        .gcircle {
          position: absolute;
          border-radius: 50%;
          filter: blur(40px);
          opacity: 0.35;
          animation: hGlowPulse 6s infinite ease-in-out;

          &.gcircle-1 {
            width: 150px;
            height: 150px;
            background: rgba(255, 255, 255, 0.3);
            top: -50px;
            left: -30px;
          }
          &.gcircle-2 {
            width: 180px;
            height: 180px;
            background: rgba(96, 165, 250, 0.5);
            bottom: -60px;
            right: 5%;
            animation-delay: 3s;
          }
        }
      }

      .header-shimmer {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.05) 50%,
          transparent 100%
        );
        background-size: 200% 100%;
        animation: hShimmerAnim 8s infinite linear;
        pointer-events: none;
        z-index: 1;
      }

      .header-light-rays {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          rgba(255, 255, 255, 0.015) 10px,
          rgba(255, 255, 255, 0.015) 20px
        );
        pointer-events: none;
        z-index: 1;
      }

      /* HEADER MAIN CONTENT */
      .header-main-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        position: relative;
        z-index: 2;

        @media (max-width: 768px) {
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
        }
      }

      .header-left {
        display: flex;
        align-items: center;

        .floating-icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.18);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 50%;
          box-shadow: 0 8px 32px rgba(15, 76, 255, 0.2);
          animation: hTruckIconFloat 3.5s infinite ease-in-out;
          flex-shrink: 0;

          @media (max-width: 600px) {
            width: 48px;
            height: 48px;
          }
        }

        .header-text-container {
          margin-left: 16px;
        }

        .header-title {
          font-size: 24px;
          font-weight: 800;
          color: #ffffff !important;
          margin: 0;
          line-height: 1.2;
          letter-spacing: -0.5px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

          @media (max-width: 600px) {
            font-size: 20px;
          }
        }

        .header-subtitle {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.9);
          display: block;
          margin-top: 4px;
          font-weight: 500;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

          @media (max-width: 600px) {
            font-size: 11px;
          }
        }
      }

      .header-right {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: center;

        @media (max-width: 768px) {
          align-items: flex-start;
        }

        .premium-status-chip {
          position: relative;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #ffffff;
          font-size: 11px;
          font-weight: 800;
          padding: 6px 14px;
          border-radius: 20px;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
          display: flex;
          align-items: center;
          gap: 6px;
          animation: hBadgePulse 2s infinite ease-in-out;

          &::before {
            content: "";
            display: inline-block;
            width: 6px;
            height: 6px;
            background: #ffffff;
            border-radius: 50%;
            box-shadow: 0 0 8px #ffffff;
          }
        }

        .physical-delivery-tag {
          font-size: 12px;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.95);
          letter-spacing: 0.2px;
          margin-top: 8px;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
      }

      /* HEADER STATS */
      .header-stats-container {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        position: relative;
        z-index: 2;
        margin-top: 8px;

        @media (max-width: 768px) {
          flex-wrap: wrap;
        }

        .stat-badge-card {
          background: rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          padding: 8px 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          cursor: pointer;

          @media (max-width: 600px) {
            padding: 6px 12px;
            flex-grow: 1;
          }

          &:hover {
            background: rgba(255, 255, 255, 0.22);
            transform: translateY(-3px);
            box-shadow: 0 6px 15px rgba(15, 76, 255, 0.15),
              0 0 0 1px rgba(255, 255, 255, 0.3);
          }

          .stat-badge-icon {
            font-size: 14px;
          }

          .stat-badge-text {
            font-size: 12px;
            font-weight: 700;
            color: #ffffff;
            letter-spacing: 0.2px;
          }
        }
      }

      /* BOTTOM STRIP */
      .header-bottom-strip {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 4px;
        background: #60a5fa;
        box-shadow: 0 -1px 8px rgba(96, 165, 250, 0.6);
        z-index: 3;
        overflow: hidden;

        .strip-light-sweep {
          position: absolute;
          top: 0;
          left: -150px;
          width: 150px;
          height: 100%;
          background: linear-gradient(90deg, transparent, #ffffff, transparent);
          animation: hStripSweep 4s infinite linear;
        }
      }
    }

    /* KEYFRAME ANIMATIONS */
    @keyframes hParticleFloat {
      0%,
      100% {
        transform: translateY(0) scale(1);
        opacity: 0.3;
      }
      50% {
        transform: translateY(-15px) scale(1.2);
        opacity: 0.8;
      }
    }

    @keyframes hBubbleFloat {
      0%,
      100% {
        transform: translateY(0) rotate(0deg);
      }
      50% {
        transform: translateY(-10px) rotate(10deg);
      }
    }

    @keyframes hGlowPulse {
      0%,
      100% {
        transform: scale(1);
        opacity: 0.35;
      }
      50% {
        transform: scale(1.15);
        opacity: 0.5;
      }
    }

    @keyframes hShimmerAnim {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }

    @keyframes hTruckIconFloat {
      0%,
      100% {
        transform: translateY(0) rotate(0deg);
      }
      50% {
        transform: translateY(-6px) rotate(-3deg);
      }
    }

    @keyframes hBadgePulse {
      0%,
      100% {
        transform: scale(1);
        box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
      }
      50% {
        transform: scale(1.05);
        box-shadow: 0 4px 25px rgba(239, 68, 68, 0.6);
      }
    }

    @keyframes hStripSweep {
      0% {
        left: -150px;
      }
      50% {
        left: 100%;
      }
      100% {
        left: 100%;
      }
    }

    @keyframes hStreakMove {
      0% {
        left: -300px;
      }
      40% {
        left: 100%;
      }
      100% {
        left: 100%;
      }
    }

    .border-light-blue {
      border-color: rgba(21, 101, 192, 0.08) !important;
    }

    .flying-icons-container {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      overflow: hidden;
      z-index: 0;

      .flying-icon {
        position: absolute;
        color: rgba(21, 101, 192, 0.05) !important;
        font-size: 32px !important;
        filter: blur(0.5px);
        pointer-events: none;
        transition: all 0.3s ease;
      }

      .icon-truck {
        top: 15%;
        left: -60px;
        animation: driftTruck 14s infinite linear;
      }

      .icon-box {
        top: 65%;
        right: 5%;
        animation: floatIcon 7s infinite ease-in-out;
      }

      .icon-plane {
        top: 40%;
        left: -60px;
        animation: driftPlane 18s infinite linear;
      }

      .icon-map {
        bottom: 12%;
        left: 35%;
        animation: floatIcon2 9s infinite ease-in-out;
      }

      .icon-email {
        top: 25%;
        right: 22%;
        animation: floatIcon 11s infinite ease-in-out;
      }
    }

    .card-gradient-border-top {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 6px;
      background: linear-gradient(90deg, #1565c0 0%, #60a5fa 50%, #1565c0 100%);
      background-size: 200% auto;
      animation: shimmerBtn 4s infinite linear;
    }

    .glow-sphere-1 {
      position: absolute;
      width: 250px;
      height: 250px;
      background: radial-gradient(
        circle,
        rgba(21, 101, 192, 0.06) 0%,
        rgba(21, 101, 192, 0) 70%
      );
      top: -50px;
      right: -50px;
      pointer-events: none;
      z-index: 0;
    }

    .glow-sphere-2 {
      position: absolute;
      width: 300px;
      height: 300px;
      background: radial-gradient(
        circle,
        rgba(96, 165, 250, 0.06) 0%,
        rgba(96, 165, 250, 0) 70%
      );
      bottom: -80px;
      left: -80px;
      pointer-events: none;
      z-index: 0;
    }

    .corner-decor-top-right {
      position: absolute;
      top: 15px;
      right: 15px;
      width: 20px;
      height: 20px;
      border-top: 2px solid rgba(21, 101, 192, 0.15);
      border-right: 2px solid rgba(21, 101, 192, 0.15);
      pointer-events: none;
    }

    .corner-decor-bottom-left {
      position: absolute;
      bottom: 15px;
      left: 15px;
      width: 20px;
      height: 20px;
      border-bottom: 2px solid rgba(21, 101, 192, 0.15);
      border-left: 2px solid rgba(21, 101, 192, 0.15);
      pointer-events: none;
    }

    .particle-track {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      overflow: hidden;
      pointer-events: none;
      z-index: 0;
    }

    .delivery-particle {
      position: absolute;
      background-color: rgba(21, 101, 192, 0.1);
      border-radius: 50%;
      pointer-events: none;
      z-index: 0;
      animation: floatDeliveryParticle 10s infinite ease-in-out;
    }
  }
}

.delivery-info-banner {
  background: linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%);
  border: 1px solid rgba(21, 101, 192, 0.1);
  border-radius: 16px;
  position: relative;
  overflow: hidden;

  .info-pulse-icon-box {
    position: relative;
    background: #ffffff;
    border-radius: 12px;
    padding: 10px;
    box-shadow: 0 8px 20px rgba(21, 101, 192, 0.08);

    .pulse-ring {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 12px;
      border: 2px solid rgba(21, 101, 192, 0.3);
      animation: iconPulseRing 2s infinite ease-out;
    }
  }

  .info-banner-title {
    font-size: 15px;
    font-weight: 800;
    color: #1e3a8a;
  }

  .info-banner-list {
    list-style-type: none;
    font-size: 13px;
    color: #1e40af;
    line-height: 1.6;

    li {
      position: relative;
      padding-left: 15px;
      margin-bottom: 4px;

      &::before {
        content: "•";
        position: absolute;
        left: 0;
        color: #1565c0;
        font-weight: bold;
      }
    }
  }
}

/* Custom premium floating label inputs */
.premium-input-wrapper {
  position: relative;
  background: rgba(255, 255, 255, 0.45);
  border: 1.5px solid rgba(148, 163, 184, 0.3);
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-sizing: border-box;
  overflow: hidden;
  backdrop-filter: blur(5px);

  &:hover {
    background: rgba(255, 255, 255, 0.85);
    border-color: rgba(21, 101, 192, 0.4);
    box-shadow: 0 4px 12px rgba(21, 101, 192, 0.04);
  }

  .input-glow-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
      rgba(21, 101, 192, 0.06) 0%,
      transparent 60%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  &.is-focused {
    border-color: #1565c0 !important;
    box-shadow: 0 0 0 4px rgba(21, 101, 192, 0.12) !important;
    background: #ffffff !important;

    .input-glow-bg {
      opacity: 1;
    }

    .input-prefix-icon {
      color: #1565c0 !important;
    }
  }

  &.is-valid {
    border-color: rgba(16, 185, 129, 0.35);

    &:hover {
      border-color: rgba(16, 185, 129, 0.6);
    }

    &.is-focused {
      border-color: #10b981 !important;
      box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12) !important;
    }

    .input-prefix-icon {
      color: #10b981 !important;
    }
  }

  &.has-error {
    border-color: #f87171 !important;
    background-color: rgba(254, 242, 242, 0.8) !important;

    .input-prefix-icon {
      color: #ef4444 !important;
    }

    .custom-floating-label {
      color: #ef4444 !important;
    }

    &:hover {
      border-color: #ef4444 !important;
    }

    &.is-focused {
      box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12) !important;
    }
  }

  &.shake-anim {
    animation: shakeInput 0.5s ease-in-out;
  }

  .input-inner {
    display: flex;
    align-items: center;
    padding: 6px 16px;
    min-height: 64px;
    position: relative;
    z-index: 1;
  }

  .input-prefix-icon {
    font-size: 20px !important;
    color: #64748b !important;
    margin-right: 12px;
    transition: color 0.3s ease;
    display: flex;
    align-items: center;
    height: 100%;
  }

  .input-field-container {
    position: relative;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .custom-input-element {
    width: 100%;
    border: none !important;
    outline: none !important;
    background: transparent !important;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    padding: 20px 0 4px 0;
    line-height: 1.4;
    box-sizing: border-box;
    box-shadow: none !important;

    &:focus,
    &:not(:placeholder-shown) {
      padding: 20px 0 4px 0;
    }
  }

  .custom-textarea-element {
    resize: none;
    min-height: 120px;
    padding: 24px 0 4px 0;
  }

  .custom-floating-label {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    color: #64748b;
    pointer-events: none;
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    font-weight: 600;
    margin: 0;
  }

  /* When focused or has value, float the label up */
  .custom-input-element:focus + .custom-floating-label,
  .custom-input-element:not(:placeholder-shown) + .custom-floating-label {
    top: 10px;
    transform: translateY(0);
    font-size: 10px;
    font-weight: 800;
    color: #1565c0;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }

  &.is-valid .custom-input-element:focus + .custom-floating-label,
  &.is-valid
    .custom-input-element:not(:placeholder-shown)
    + .custom-floating-label {
    color: #10b981;
  }

  &.premium-input-wrapper-textarea {
    .input-inner {
      align-items: flex-start;
    }

    .input-prefix-icon {
      margin-top: 14px;
    }

    .custom-floating-label {
      top: 24px;
      transform: none;
    }

    .custom-input-element:focus + .custom-floating-label,
    .custom-input-element:not(:placeholder-shown) + .custom-floating-label {
      top: 6px;
      transform: none;
    }
  }

  .input-suffix-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 12px;
    min-width: 24px;
    height: 100%;
  }

  .valid-checkmark {
    font-size: 20px !important;
  }

  .custom-input-hint {
    display: block;
    font-size: 11px;
    color: #94a3b8;
    padding: 0 16px 10px 48px;
    margin-top: -2px;
    font-weight: 500;
    line-height: 1.4;
    pointer-events: none;
  }
}

.border-left-premium-desktop {
  @media (min-width: 960px) {
    border-left: 1px dashed rgba(21, 101, 192, 0.18) !important;
  }
}

.delivery-preview-sticky {
  position: sticky;
  top: 24px;
  z-index: 1;

  .preview-header {
    .preview-title {
      font-size: 16px;
      font-weight: 800;
      color: #1e293b;
      letter-spacing: -0.3px;
    }

    .verification-badge {
      background: #d1fae5;
      color: #065f46;
      border: 1px solid rgba(16, 185, 129, 0.2);
      border-radius: 20px;
      padding: 4px 10px;
      font-size: 11px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      animation: bounceBadge 0.5s ease;
    }
  }

  .preview-card-wrap {
    position: relative;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(
      135deg,
      rgba(21, 101, 192, 0.05) 0%,
      rgba(96, 165, 250, 0.05) 100%
    );

    .preview-card-bg-glow {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(21, 101, 192, 0.15) 0%,
        rgba(96, 165, 250, 0.15) 100%
      );
      border-radius: 16px;
      filter: blur(10px);
      opacity: 0.3;
      z-index: 0;
    }

    .preview-card {
      position: relative;
      background: rgba(255, 255, 255, 0.85);
      border-radius: 14px;
      z-index: 1;
      border: 1.5px dashed rgba(21, 101, 192, 0.15);
      overflow: hidden;

      &::after {
        content: "🚚";
        position: absolute;
        bottom: 10px;
        right: 15px;
        font-size: 48px;
        opacity: 0.05;
        pointer-events: none;
        z-index: 0;
      }

      .stamp-verified-badge {
        position: absolute;
        top: 15px;
        right: 15px;
        width: 68px;
        height: 68px;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
        pointer-events: none;

        .stamp-svg {
          position: absolute;
          width: 100%;
          height: 100%;
          animation: rotateStamp 20s infinite linear;
        }

        .stamp-center-icon {
          font-size: 24px !important;
          position: absolute;
          z-index: 2;
          background: #ffffff;
          border-radius: 50%;
          box-shadow: 0 4px 10px rgba(16, 185, 129, 0.2);
        }
      }

      .preview-lbl {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        font-weight: 700;
        color: #94a3b8;
        position: relative;
        z-index: 1;
      }

      .preview-val {
        font-size: 14px;
        font-weight: 700;
        display: block;
        min-height: 20px;
        word-break: break-word;
        position: relative;
        z-index: 1;

        &.empty {
          color: #94a3b8;
          font-style: italic;
          font-weight: 500;
        }

        &.filled {
          color: #0f172a;
        }
      }

      .address-val-preview {
        white-space: pre-line;
        line-height: 1.5;
      }
    }
  }

  .conversion-alert {
    background: #fff5f5;
    border-radius: 12px;
    border: 1px solid rgba(239, 68, 68, 0.12);

    .alert-txt {
      font-size: 12px;
      color: #991b1b;
      line-height: 1.4;

      strong {
        color: #ef4444;
      }
    }
  }
}

.checkout-btn-disabled {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
  animation: none !important;
  pointer-events: auto !important;
}

@keyframes floatDeliveryParticle {
  0%,
  100% {
    transform: translateY(0) translateX(0);
  }
  50% {
    transform: translateY(-20px) translateX(15px);
  }
}

@keyframes iconPulseRing {
  0% {
    transform: scale(0.95);
    opacity: 1;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

@keyframes bounceBadge {
  0% {
    transform: scale(0.3);
  }
  50% {
    transform: scale(1.1);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes rotateStamp {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes shakeInput {
  0%,
  100% {
    transform: translateX(0);
  }
  20%,
  60% {
    transform: translateX(-6px);
  }
  40%,
  80% {
    transform: translateX(6px);
  }
}

/* --- WARNING DIALOG CLASS --- */
.premium-warning-dialog {
  border-radius: 24px !important;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(239, 68, 68, 0.25) !important;
  backdrop-filter: blur(8px);

  .warning-dialog-card {
    border-radius: 24px !important;
    background: rgba(255, 255, 255, 0.95) !important;
    position: relative;
    border: 1px solid rgba(239, 68, 68, 0.15);

    .warning-header-glow {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 60px;
      background: radial-gradient(
        circle,
        rgba(239, 68, 68, 0.1) 0%,
        rgba(239, 68, 68, 0) 70%
      );
      pointer-events: none;
    }

    .warning-icon-box {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 80px;
      height: 80px;
      background: #fff5f5;
      border-radius: 50%;
      position: relative;

      .warning-pulse {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 50%;
        border: 2px solid rgba(239, 68, 68, 0.3);
        animation: warningPulse 1.8s infinite ease-out;
      }
    }

    .dialog-warning-title {
      font-size: 20px;
      font-weight: 800;
      color: #1e293b;
      letter-spacing: -0.5px;
    }

    .dialog-warning-desc {
      font-size: 14px;
      color: #475569;
      line-height: 1.5;

      strong {
        color: #ef4444;
      }
    }

    .dialog-confirm-btn {
      text-transform: none;
      letter-spacing: normal;
      font-size: 15px;
      font-weight: 800;
      height: 48px !important;
      box-shadow: 0 4px 15px rgba(21, 101, 192, 0.2) !important;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(21, 101, 192, 0.3) !important;
      }
    }
  }
}

@keyframes warningPulse {
  0% {
    transform: scale(0.95);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

/* --- FLYING SHIPPING ICONS KEYFRAMES --- */
@keyframes driftTruck {
  0% {
    transform: translateX(0) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 0.8;
  }
  90% {
    opacity: 0.8;
  }
  100% {
    transform: translateX(750px) rotate(4deg);
    opacity: 0;
  }
}

@keyframes driftPlane {
  0% {
    transform: translate(0, 80px) rotate(-12deg);
    opacity: 0;
  }
  15% {
    opacity: 0.6;
  }
  85% {
    opacity: 0.6;
  }
  100% {
    transform: translate(800px, -140px) rotate(-12deg);
    opacity: 0;
  }
}

@keyframes floatIcon {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-12px) rotate(8deg);
  }
}

@keyframes floatIcon2 {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(-8deg);
  }
}

/* --- Added Animations and Styles for Collapsible Panels --- */
.rotate-180 {
  transform: rotate(180deg) !important;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes floatEffect3D {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-12px) rotate(3deg);
  }
}

.member-collapsed-summary {
  cursor: pointer;
  padding: 14px 20px;
  background: rgba(241, 245, 249, 0.6);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 12px;
  position: relative;
  overflow: hidden;

  &:hover {
    background: #ffffff;
    border-color: rgba(21, 101, 192, 0.25);
    box-shadow: 0 4px 15px rgba(21, 101, 192, 0.05);
  }

  .collapsed-member-name {
    font-size: 16px;
    font-weight: 800;
    color: #1e293b;
  }

  .collapsed-member-lom {
    font-size: 13.5px;
    color: #64748b;
    font-weight: 600;
  }

  .collapsed-location-text {
    font-size: 14.5px;
    color: #1e293b;
    font-weight: 700;
  }
}
</style>
